import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type AssistantObservation, type QuoteRequestData } from '../api';
import { brisbaneInputToIso } from '../lib/brisbane';
import { ResponsiveList } from '../components/ResponsiveList';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { ObservationImages } from './Assistant';
import { clampPage } from '../lib/clampPage';

const pageCardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--spacing-4)',
  boxShadow: 'var(--shadow-sm)',
};

const primaryButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  backgroundColor: 'var(--color-accent)',
  border: 'none',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text-inverse)',
  fontWeight: 600,
  minHeight: 44,
};

const secondaryButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  backgroundColor: 'var(--color-surface-elevated)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
  fontWeight: 500,
  minHeight: 44,
};

const searchInputStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 360,
  minHeight: 44,
  padding: 'var(--spacing-2) var(--spacing-3)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  marginBottom: 'var(--spacing-3)',
};

// "‹ Prev / Page X of Y / Next ›" pagination control style, matching the
// existing convention used by Contacts → Online's New Quote Requests-
// equivalent pagination (Contacts.tsx).
const smallButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-1) var(--spacing-2)',
  fontSize: 'var(--font-size-xs)',
  borderRadius: 'var(--radius-default)',
  border: '1px solid var(--color-border)',
  backgroundColor: 'var(--color-surface-elevated)',
  color: 'var(--color-text)',
  cursor: 'pointer',
};

// New Quote Requests (website Quote Tool submissions) — same 15/page
// convention as Contacts → Online.
const QUOTE_REQUESTS_PAGE_SIZE = 15;

/** Case-insensitive match of a search term against a set of fields. */
function matchesSearch(term: string, fields: Array<unknown>): boolean {
  const q = term.trim().toLowerCase();
  if (!q) return true;
  return fields.some((f) => String(f ?? '').toLowerCase().includes(q));
}

const tabStyle = (active: boolean): React.CSSProperties => ({
  padding: 'var(--spacing-2) var(--spacing-4)',
  backgroundColor: active ? 'var(--color-surface-elevated)' : 'transparent',
  border: '1px solid',
  borderColor: active ? 'var(--color-accent)' : 'var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: active ? 'var(--color-text)' : 'var(--color-text-muted)',
  fontWeight: active ? 600 : 500,
  minHeight: 44,
});

type Tab = 'requests' | 'unscheduled' | 'scheduled' | 'quotes' | 'invoices';

function money(total: unknown): string {
  return total != null ? `$${Number(total).toFixed(2)}` : '—';
}

/** Brisbane date + time label for a scheduled job's ISO start. */
function bneDateTime(iso: string): string {
  return new Intl.DateTimeFormat('en-AU', {
    timeZone: 'Australia/Brisbane',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(new Date(iso));
}

/** Deep link to a Xero quote/invoice by its Xero document id (GUID). */
function xeroDocUrl(kind: 'quote' | 'invoice', xeroId?: string): string | null {
  if (!xeroId) return null;
  return kind === 'quote'
    ? `https://go.xero.com/app/quotes/view/${xeroId}`
    : `https://go.xero.com/app/invoicing/view/${xeroId}`;
}

const docLinkStyle: React.CSSProperties = {
  color: 'var(--color-accent)',
  fontWeight: 600,
  textDecoration: 'none',
};

/** Render a document number as a link to Xero (falls back to plain text). */
function DocNumber({ kind, row }: { kind: 'quote' | 'invoice'; row: any }) {
  const url = xeroDocUrl(kind, row.xero_id);
  const label = row.number ?? '—';
  if (!url) return <>{label}</>;
  return (
    <a href={url} target="_blank" rel="noreferrer" style={docLinkStyle} title="Open in Xero ↗">
      {label} ↗
    </a>
  );
}

function moneyTotal(n: number): string {
  return `$${n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const chipStyle = (active: boolean): React.CSSProperties => ({
  padding: 'var(--spacing-1) var(--spacing-3)',
  backgroundColor: active ? 'var(--color-accent)' : 'var(--color-surface-elevated)',
  border: '1px solid',
  borderColor: active ? 'var(--color-accent)' : 'var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: active ? 'var(--color-text-inverse)' : 'var(--color-text-muted)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  minHeight: 32,
  cursor: 'pointer',
});

/**
 * Quotes / Invoices view: a totals summary, status filter chips, and the
 * dual-render list. Both document types share the same shape.
 */
function DocTab({
  kind,
  rows,
  loading,
  search,
  contactIdFilter,
  onAcceptAndBook,
  onOpenContact,
  onOpen,
}: {
  kind: 'quote' | 'invoice';
  rows: any[];
  loading: boolean;
  search: string;
  contactIdFilter?: string | null;
  onAcceptAndBook?: (row: any) => void;
  onOpenContact?: (name?: string | null, xeroContactId?: string | null) => void;
  onOpen?: (row: any) => void;
}) {
  const [status, setStatus] = useState('ALL');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Always offer the standard statuses (so e.g. DRAFT is filterable even when
  // there are currently zero draft quotes), plus any unexpected ones present.
  const statuses = useMemo(() => {
    const canonical = kind === 'invoice'
      ? ['DRAFT', 'SUBMITTED', 'AUTHORISED', 'PAID', 'VOIDED', 'DELETED']
      : ['DRAFT', 'SENT', 'ACCEPTED', 'INVOICED', 'DECLINED', 'DELETED'];
    const present = Array.from(new Set(rows.map((r) => r.status).filter(Boolean)));
    const extras = present.filter((s) => !canonical.includes(s)).sort();
    return ['ALL', ...canonical, ...extras];
  }, [rows, kind]);
  // Rows passing the date + search + contact filters (but NOT the status chip),
  // so the chip counts reflect the current date window.
  const base = useMemo(
    () =>
      rows.filter((r) => {
        const d = String(r.doc_date || '').slice(0, 10);
        if (fromDate && d < fromDate) return false;
        if (toDate && d > toDate) return false;
        return contactIdFilter
          ? r.contact_id === contactIdFilter
          : matchesSearch(search, [r.number, r.contact_name, r.status, r.doc_date]);
      }),
    [rows, search, contactIdFilter, fromDate, toDate],
  );
  const filtered = useMemo(() => base.filter((r) => status === 'ALL' || r.status === status), [base, status]);
  const statusCounts = useMemo(() => {
    const m: Record<string, number> = { ALL: base.length };
    for (const r of base) { const s = String(r.status || '').toUpperCase(); m[s] = (m[s] || 0) + 1; }
    return m;
  }, [base]);
  const total = useMemo(
    () => filtered.reduce((sum, r) => sum + (Number(r.total) || 0), 0),
    [filtered],
  );

  if (loading) return <p style={{ color: 'var(--color-text-muted)' }}>Loading…</p>;

  return (
    <>
      {/* Totals at the top */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--spacing-6)',
          padding: 'var(--spacing-3) var(--spacing-4)',
          marginBottom: 'var(--spacing-3)',
          backgroundColor: 'var(--color-surface-elevated)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-default)',
        }}
      >
        <div>
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {status === 'ALL' ? `Total ${kind}s` : `${status} ${kind}s`}
          </div>
          <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700 }}>{filtered.length}</div>
        </div>
        <div>
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Total value
          </div>
          <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--color-accent)' }}>
            {moneyTotal(total)}
          </div>
        </div>
      </div>

      {/* Date-range filter */}
      <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center', marginBottom: 'var(--spacing-2)', flexWrap: 'wrap', fontSize: 'var(--font-size-sm)' }}>
        <span style={{ color: 'var(--color-text-muted)' }}>Dates</span>
        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={{ ...searchInputStyle, maxWidth: 170, marginBottom: 0 }} title="From date" />
        <span style={{ color: 'var(--color-text-muted)' }}>→</span>
        <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={{ ...searchInputStyle, maxWidth: 170, marginBottom: 0 }} title="To date" />
        {(fromDate || toDate) && (
          <button style={{ ...secondaryButtonStyle, minHeight: 32, padding: '2px 10px' }} onClick={() => { setFromDate(''); setToDate(''); }}>Clear dates</button>
        )}
      </div>

      {/* Status filter chips (with counts for the current date window) */}
      <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-3)', flexWrap: 'wrap' }}>
        {statuses.map((s) => (
          <button key={s} onClick={() => setStatus(s)} style={chipStyle(status === s)}>
            {s === 'ALL' ? 'All' : s}{statusCounts[s] ? ` (${statusCounts[s]})` : ''}
          </button>
        ))}
      </div>

      <ResponsiveList
        rows={filtered}
        getRowKey={(r) => r.xero_id}
        emptyMessage={`No ${kind}s${status === 'ALL' ? '' : ` with status ${status}`}.`}
        columns={[
          { key: 'number', label: 'Number', primaryOnMobile: true, render: (r) => <DocNumber kind={kind} row={r} /> },
          { key: 'contact', label: 'Contact', render: (r) => (
            <div>
              {r.contact_name && onOpenContact ? (
                <button onClick={() => onOpenContact(r.contact_name, r.contact_id)} title="Open this contact"
                  style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'var(--color-accent)', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline', textAlign: 'left' }}>
                  {r.contact_name}
                </button>
              ) : (r.contact_name ?? '—')}
              {kind === 'quote' && r.referral && (
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>↪ Referral: {r.referral}</div>
              )}
            </div>
          ) },
          { key: 'status', label: 'Status', render: (r) => r.status ?? '—' },
          { key: 'date', label: 'Date', render: (r) => (
            <div>
              <div>{r.doc_date ?? '—'}</div>
              {kind === 'quote' && r.booked_at && (
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-accent)' }}>📅 Booked {String(r.booked_at).slice(0, 10)}</div>
              )}
              {kind === 'quote' && !r.booked_at && r.status === 'ACCEPTED' && (
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-error)' }}>not on the calendar</div>
              )}
            </div>
          ) },
          { key: 'total', label: 'Total', align: 'right', render: (r) => money(r.total) },
          ...(onOpen || onAcceptAndBook
            ? [
                {
                  key: 'action',
                  label: '',
                  align: 'right' as const,
                  render: (r: any) => (
                    <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      {onOpen && (
                        <button style={secondaryButtonStyle} onClick={() => onOpen(r)} title={`Open and send this ${kind} from the portal`}>
                          Open
                        </button>
                      )}
                      {onAcceptAndBook && (r.status === 'SENT' || (r.status === 'ACCEPTED' && !r.booked_at)) && (
                        <button style={primaryButtonStyle} onClick={() => onAcceptAndBook(r)}>
                          {r.status === 'SENT' ? 'Mark accepted & book' : 'Book'}
                        </button>
                      )}
                    </div>
                  ),
                },
              ]
            : []),
        ]}
      />
    </>
  );
}

export function Billing() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [tab, setTab] = useState<Tab>('unscheduled');
  const [search, setSearch] = useState('');
  // When a contact deep-links by their Xero id we filter on that (reliable),
  // not on the display name — a quote can be under a different name.
  const [contactFilter, setContactFilter] = useState<{ id: string; label: string } | null>(null);

  // Deep link from a contact: /billing?tab=quotes&xeroContact=<id>&label=<name>
  // (or ?q=<name> as a fallback) opens that tab, filtered to the customer.
  useEffect(() => {
    const t = sp.get('tab');
    const q = sp.get('q');
    const xeroContact = sp.get('xeroContact');
    const label = sp.get('label');
    const quote = sp.get('quote'); // /billing?quote=<xeroId> opens that quote (Analytics → recent requests)
    if (t === 'quotes' || t === 'invoices' || t === 'scheduled' || t === 'unscheduled') setTab(t);
    if (xeroContact) setContactFilter({ id: xeroContact, label: label || 'this contact' });
    else if (q) setSearch(q);
    if (quote) { setTab('quotes'); setOpenQuoteId(quote); }
    if (t || q || xeroContact || quote) setSp({}, { replace: true });
    // run once on mount to pick up ?tab/?q/?xeroContact/?quote hand-off params
  }, []);
  // Open the contact's detail directly. Prefer the Xero contact id (exact match);
  // the name comes along so the Contacts list can load results to open from.
  const openContact = (name?: string | null, xeroContactId?: string | null) => {
    const params = new URLSearchParams();
    if (xeroContactId) params.set('xeroContact', xeroContactId);
    if (name) params.set('q', name);
    if (![...params.keys()].length) return;
    navigate(`/contacts?${params.toString()}`);
  };

  const { data: status } = useQuery({ queryKey: ['xero-status'], queryFn: () => api.xeroStatus() });
  const { data: quotes = [], isLoading: quotesLoading } = useQuery({
    queryKey: ['billing-quotes'],
    queryFn: () => api.billingQuotes(),
  });
  const { data: invoices = [], isLoading: invoicesLoading } = useQuery({
    queryKey: ['billing-invoices'],
    queryFn: () => api.billingInvoices(),
  });
  const { data: unscheduled = [], isLoading: unscheduledLoading } = useQuery({
    queryKey: ['billing-unscheduled'],
    queryFn: () => api.billingUnscheduled(),
  });
  const { data: scheduled = [], isLoading: scheduledLoading } = useQuery({
    queryKey: ['billing-scheduled'],
    queryFn: () => api.billingScheduled(),
  });
  // "New Quote Requests" (website Quote Tool submissions awaiting review).
  // category='quote_request' is ALSO used for genuine emailed quote requests
  // picked up by the Gmail scanner (the public endpoint never sets gmailId;
  // the email scanner always does — see assistant.ts#createObservation call
  // sites), so `source:'website'` (→ gmail_id IS NULL server-side) is what
  // narrows this down to website Quote Tool submissions specifically —
  // filtered in SQL, before pagination, not client-side after the fact (see
  // assistant.ts#listObservationsPaginated). No 200-row ceiling, unlike the
  // legacy ['assistant-observations','new'] query Assistant.tsx still uses
  // for its own, unrelated mixed-category feed.
  const [quoteRequestsPage, setQuoteRequestsPage] = useState(1);
  const { data: quoteRequestsData } = useQuery({
    queryKey: ['assistant-observations', 'quote_request', 'website', 'new', quoteRequestsPage, QUOTE_REQUESTS_PAGE_SIZE],
    queryFn: () =>
      api.listAssistantObservationsPaginated({ status: 'new', category: 'quote_request', source: 'website', page: quoteRequestsPage, pageSize: QUOTE_REQUESTS_PAGE_SIZE }),
    refetchInterval: 30_000,
    // The global QueryClient default (main.tsx) sets staleTime to 5 minutes,
    // which normally suppresses refetchOnWindowFocus until data is "stale" —
    // overriding it here is what actually makes tab-switch-back refresh work
    // promptly instead of silently doing nothing for up to 5 minutes.
    staleTime: 0,
    // Keep polling even if this browser tab isn't the focused one (e.g. the
    // admin is testing by submitting from a second, focused website tab).
    refetchIntervalInBackground: true,
  });
  const websiteQuoteRequests = quoteRequestsData?.items ?? [];
  const quoteRequestsTotal = quoteRequestsData?.total ?? 0;
  const quoteRequestsTotalPages = quoteRequestsData?.totalPages ?? 1;
  // Requests tab sub-view: New (pending) vs Handled (archived history).
  const [requestsSubView, setRequestsSubView] = useState<'new' | 'handled'>('new');
  const [handledPage, setHandledPage] = useState(1);
  const { data: handledRequestsData } = useQuery({
    queryKey: ['assistant-observations', 'quote_request', 'website', 'archived', handledPage, QUOTE_REQUESTS_PAGE_SIZE],
    queryFn: () =>
      api.listAssistantObservationsPaginated({ status: 'archived', category: 'quote_request', source: 'website', page: handledPage, pageSize: QUOTE_REQUESTS_PAGE_SIZE }),
    enabled: tab === 'requests' && requestsSubView === 'handled',
  });
  const handledRequests = handledRequestsData?.items ?? [];
  const handledTotal = handledRequestsData?.total ?? 0;
  const handledTotalPages = handledRequestsData?.totalPages ?? 1;
  // A request being turned into a quote (opens CreateQuoteModal pre-filled).
  const [createFromRequest, setCreateFromRequest] = useState<AssistantObservation | null>(null);
  // Clamp after Mark-handled/refetch shrinks the count out from under the
  // current page (e.g. handling the only item on page 3 of 3 → 2 pages —
  // this brings the page back to 2 instead of rendering "Page 3 of 2" or an
  // empty page). A no-op whenever the count grows/stays the same (a new
  // incoming request never forces the page back to 1).
  useEffect(() => {
    setQuoteRequestsPage((p) => clampPage(p, quoteRequestsTotalPages));
  }, [quoteRequestsTotalPages]);

  // Holds the actual selected row (captured at "Open" time), not just an id
  // — the item lives on whichever server page is currently loaded, and
  // there is no longer a full unpaginated array to re-search it in.
  const [openQuoteRequest, setOpenQuoteRequest] = useState<AssistantObservation | null>(null);
  const archiveQuoteRequestMutation = useMutation({
    mutationFn: (id: number) => api.archiveAssistantObservation(id),
    onSuccess: () => {
      setOpenQuoteRequest(null);
      // Invalidates every ['assistant-observations', ...] query — the new
      // paginated Billing/sidebar-badge queries above (so the page that
      // just lost an item and the badge both refetch) and the legacy shared
      // query Assistant.tsx uses (harmless — it simply refetches data that
      // didn't change).
      queryClient.invalidateQueries({ queryKey: ['assistant-observations'] });
    },
  });

  const syncMutation = useMutation({
    mutationFn: () => api.billingSync(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing-quotes'] });
      queryClient.invalidateQueries({ queryKey: ['billing-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['billing-unscheduled'] });
      queryClient.invalidateQueries({ queryKey: ['billing-scheduled'] });
    },
  });

  const connectMutation = useMutation({
    mutationFn: () => api.xeroStartConnect(),
    onSuccess: (res) => {
      if (res.authorizeUrl) window.location.href = res.authorizeUrl;
    },
  });

  const [calendarNote, setCalendarNote] = useState<string | null>(null);
  const [showCreateQuote, setShowCreateQuote] = useState(false);
  const [openQuoteId, setOpenQuoteId] = useState<string | null>(null);
  const [openInvoiceId, setOpenInvoiceId] = useState<string | null>(null);
  const [pendingPreWash, setPendingPreWash] = useState<{ bookingId: string; name: string; email: string | null; when: string } | null>(null);
  const preWashMutation = useMutation({
    mutationFn: (bookingId: string) => api.sendPreWashReminder(bookingId),
    onSuccess: (res) => {
      setPendingPreWash(null);
      queryClient.invalidateQueries({ queryKey: ['billing-scheduled'] });
      setCalendarNote(`Pre-wash reminder sent to ${res.to} for ${res.date}.`);
    },
    onError: (e: any) => {
      setPendingPreWash(null);
      setCalendarNote(`Could not send pre-wash reminder: ${e.message}`);
    },
  });
  const calendarSyncMutation = useMutation({
    mutationFn: () => api.calendarSync(),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['billing-scheduled'] });
      const changes = (res.imported || 0) + (res.updated || 0) + (res.removed || 0) + (res.pushed || 0) + (res.enriched || 0);
      setCalendarNote(
        changes === 0
          ? 'Calendar in sync — nothing to change.'
          : `Synced: ${res.imported} imported, ${res.updated} updated, ${res.removed} removed, ${res.pushed} pushed, ${res.enriched} services added.`,
      );
    },
    onError: (e: any) => setCalendarNote(`Calendar sync failed: ${e.message}`),
  });

  const remindMutation = useMutation({ mutationFn: (customerId: number) => api.billingRemind(customerId) });

  // "Accepted → calendar" scheduling modal state
  const [scheduling, setScheduling] = useState<{
    contactId: string;
    name: string;
    docNumber?: string | null;
    address?: string | null;
    docId?: string | null;
    docType?: 'quote' | 'invoice' | null;
    acceptQuoteId?: string | null; // set when booking from a SENT quote (accept + book)
  } | null>(null);
  const [scheduleAt, setScheduleAt] = useState('');
  const [scheduleHours, setScheduleHours] = useState(3); // duration in hours
  const [scheduleAddress, setScheduleAddress] = useState('');
  // Prefill the address from the contact when the dialog opens.
  useEffect(() => {
    if (scheduling) setScheduleAddress(scheduling.address ?? '');
  }, [scheduling]);
  const scheduleMutation = useMutation({
    mutationFn: (data: {
      contactId: string;
      startTime: string;
      durationMins: number;
      summary?: string;
      location?: string | null;
      docId?: string | null;
      docType?: 'quote' | 'invoice' | null;
    }) => api.billingSchedule(data),
    onSuccess: (res) => {
      setScheduling(null);
      setScheduleAt('');
      queryClient.invalidateQueries({ queryKey: ['billing-unscheduled'] });
      queryClient.invalidateQueries({ queryKey: ['billing-scheduled'] });
      const span = (res.days ?? 1) > 1 ? ` (split across ${res.days} days)` : '';
      setCalendarNote(
        res.calendar?.success
          ? `Added to the team Google Calendar${span}.`
          : `Booked in the portal${span}, but the Google Calendar event was not created: ${res.calendar?.error ?? 'calendar not configured'}`,
      );
    },
  });

  // Accept a SENT quote in Xero + book it (from the Quotes tab button).
  const acceptBookMutation = useMutation({
    mutationFn: (data: { quoteId: string; startTime: string; durationMins: number; summary?: string; location?: string | null }) =>
      api.acceptAndBookQuote(data.quoteId, {
        startTime: data.startTime,
        durationMins: data.durationMins,
        summary: data.summary,
        location: data.location,
      }),
    onSuccess: (res) => {
      setScheduling(null);
      setScheduleAt('');
      queryClient.invalidateQueries({ queryKey: ['billing-quotes'] });
      queryClient.invalidateQueries({ queryKey: ['billing-unscheduled'] });
      queryClient.invalidateQueries({ queryKey: ['billing-scheduled'] });
      const span = (res.days ?? 1) > 1 ? ` (split across ${res.days} days)` : '';
      setCalendarNote(
        res.calendar?.success
          ? `Quote accepted in Xero and the job added to the calendar${span}.`
          : `Quote accepted and booked${span}, but the calendar event was not created: ${res.calendar?.error ?? 'calendar not configured'}`,
      );
    },
    onError: (e: any) => setCalendarNote(`Could not accept & book: ${e.message}`),
  });

  const connected = status?.connected;
  const [unschedStatus, setUnschedStatus] = useState('ACCEPTED');
  const unschedStatuses = useMemo(
    () => ['ALL', ...Array.from(new Set(unscheduled.map((c) => c.status).filter(Boolean))).sort()],
    [unscheduled],
  );
  const filteredUnscheduled = useMemo(
    () =>
      unscheduled.filter(
        (c) =>
          (unschedStatus === 'ALL' || c.status === unschedStatus) &&
          matchesSearch(search, [c.contactName, c.address, c.status]),
      ),
    [unscheduled, search, unschedStatus],
  );
  const filteredScheduled = useMemo(
    () => scheduled.filter((s) => matchesSearch(search, [s.customerName, s.docNumber, s.address])),
    [scheduled, search],
  );
  const tabs: { key: Tab; label: string; badge?: number }[] = [
    { key: 'requests', label: 'New Website Quote Requests', badge: quoteRequestsTotal },
    { key: 'unscheduled', label: 'Not yet scheduled' },
    { key: 'scheduled', label: 'Scheduled' },
    { key: 'quotes', label: 'Quotes' },
    { key: 'invoices', label: 'Invoices' },
  ];

  return (
    <div className="billing-page">
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 'var(--spacing-4)',
          marginBottom: 'var(--spacing-4)',
          flexWrap: 'wrap',
        }}
      >
        <h1>Billing</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>
          {connected ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
              <span style={{ color: 'var(--color-success)', fontWeight: 600 }}>● Xero connected</span>
              <button
                onClick={() => connectMutation.mutate()}
                disabled={connectMutation.isPending}
                style={{ ...secondaryButtonStyle, minHeight: 32, fontSize: 'var(--font-size-xs)' }}
                title="Re-consent to grant new permissions (e.g. accepting quotes)"
              >
                {connectMutation.isPending ? 'Reconnecting…' : 'Reconnect'}
              </button>
            </span>
          ) : (
            <button
              onClick={() => connectMutation.mutate()}
              disabled={connectMutation.isPending}
              style={secondaryButtonStyle}
            >
              {connectMutation.isPending ? 'Connecting…' : 'Connect Xero'}
            </button>
          )}
          <button
            onClick={() => syncMutation.mutate()}
            disabled={!connected || syncMutation.isPending}
            style={{ ...primaryButtonStyle, opacity: !connected || syncMutation.isPending ? 0.6 : 1 }}
          >
            {syncMutation.isPending ? 'Fetching…' : 'Fetch from Xero'}
          </button>
          <button
            onClick={() => calendarSyncMutation.mutate()}
            disabled={calendarSyncMutation.isPending}
            style={secondaryButtonStyle}
            title="Two-way sync: import Google events, push portal bookings, keep both sides matched"
          >
            {calendarSyncMutation.isPending ? 'Syncing…' : 'Sync now'}
          </button>
          <button
            onClick={() => setShowCreateQuote(true)}
            disabled={!connected}
            style={{ ...primaryButtonStyle, opacity: connected ? 1 : 0.6 }}
            title={connected ? 'Create a new quote in Xero' : 'Connect Xero first'}
          >
            ＋ New quote
          </button>
        </div>
      </div>
      {showCreateQuote && (
        <CreateQuoteModal
          onClose={() => setShowCreateQuote(false)}
          onCreated={(newId) => {
            setShowCreateQuote(false);
            queryClient.invalidateQueries({ queryKey: ['billing-quotes'] });
            // Keep the user in the quote: open the just-created quote's editable
            // view (price calculator, add services, send) instead of the list.
            if (newId) setOpenQuoteId(newId);
          }}
        />
      )}

      {openQuoteId && (
        <QuotePortalModal
          key={openQuoteId}
          quoteId={openQuoteId}
          // Refresh the list on close: opening a quote self-heals its local status
          // from Xero, so re-reading the list here reflects the corrected status.
          onClose={() => { setOpenQuoteId(null); queryClient.invalidateQueries({ queryKey: ['billing-quotes'] }); }}
          onChanged={() => queryClient.invalidateQueries({ queryKey: ['billing-quotes'] })}
          onDuplicated={(newId) => { queryClient.invalidateQueries({ queryKey: ['billing-quotes'] }); setOpenQuoteId(newId); }}
        />
      )}

      {openInvoiceId && (
        <InvoicePortalModal
          key={openInvoiceId}
          invoiceId={openInvoiceId}
          onClose={() => setOpenInvoiceId(null)}
          onChanged={() => queryClient.invalidateQueries({ queryKey: ['billing-invoices'] })}
        />
      )}

      {/* Sync feedback banners */}
      {syncMutation.isError && (
        <div
          style={{
            ...pageCardStyle,
            marginBottom: 'var(--spacing-4)',
            color: 'var(--color-error)',
            backgroundColor: 'rgba(220, 53, 69, 0.08)',
            borderColor: 'rgba(220, 53, 69, 0.25)',
          }}
        >
          {(syncMutation.error as Error).message}
        </div>
      )}
      {syncMutation.isSuccess && (
        <div style={{ ...pageCardStyle, marginBottom: 'var(--spacing-4)' }}>
          Synced {syncMutation.data.quotes} quotes and {syncMutation.data.invoices} invoices.
          {syncMutation.data.warnings?.length > 0 && (
            <ul style={{ margin: 'var(--spacing-2) 0 0', color: 'var(--color-text-muted)' }}>
              {syncMutation.data.warnings.map((w: string) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Scheduling / calendar feedback */}
      {calendarNote && (
        <div
          style={{
            ...pageCardStyle,
            marginBottom: 'var(--spacing-4)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 'var(--spacing-3)',
          }}
        >
          <span>{calendarNote}</span>
          <button style={secondaryButtonStyle} onClick={() => setCalendarNote(null)}>
            Dismiss
          </button>
        </div>
      )}

      {openQuoteRequest != null && (
        <QuoteRequestModal
          observation={openQuoteRequest}
          onClose={() => setOpenQuoteRequest(null)}
          onArchive={() => archiveQuoteRequestMutation.mutate(openQuoteRequest.id)}
          onCreateQuote={() => { setCreateFromRequest(openQuoteRequest); setOpenQuoteRequest(null); }}
          onOpenQuote={(quoteId) => { setOpenQuoteRequest(null); setOpenQuoteId(quoteId); }}
          archiving={archiveQuoteRequestMutation.isPending}
        />
      )}

      {createFromRequest && (
        <CreateQuoteModal
          fromRequest={(createFromRequest.data as QuoteRequestData) ?? null}
          observationId={createFromRequest.id}
          onClose={() => setCreateFromRequest(null)}
          onCreated={(newId) => {
            // The request has become a real quote, so archive it (moves New to Handled):
            // then open the new quote.
            archiveQuoteRequestMutation.mutate(createFromRequest.id);
            setCreateFromRequest(null);
            if (newId) setOpenQuoteId(newId);
          }}
        />
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-4)', flexWrap: 'wrap' }}>
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{ ...tabStyle(tab === t.key), display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
            {t.label}
            {t.badge ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 20, height: 20, padding: '0 6px', borderRadius: 10, background: 'var(--color-error, #EE2937)', color: '#fff', fontSize: 'var(--font-size-xs)', fontWeight: 700, lineHeight: 1 }}>
                {t.badge}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {tab === 'requests' ? null : contactFilter ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-3)', padding: 'var(--spacing-2) var(--spacing-3)', background: 'var(--color-accent-muted)', borderRadius: 'var(--radius-default)', fontSize: 'var(--font-size-sm)', flexWrap: 'wrap' }}>
          <span>📌 Showing {tab} for <strong>{contactFilter.label}</strong> (linked in Xero)</span>
          <button onClick={() => setContactFilter(null)} style={{ background: 'none', border: 'none', color: 'var(--color-accent)', cursor: 'pointer', fontWeight: 600 }}>Clear filter</button>
        </div>
      ) : (
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={
            tab === 'unscheduled' || tab === 'scheduled'
              ? 'Search customer, number, address…'
              : `Search ${tab === 'quotes' ? 'quote' : 'invoice'} number, contact, status…`
          }
          aria-label="Search billing"
          style={searchInputStyle}
        />
      )}

      <section style={pageCardStyle}>
        {tab === 'requests' && (() => {
          const isNew = requestsSubView === 'new';
          const rows = isNew ? websiteQuoteRequests : handledRequests;
          const page = isNew ? quoteRequestsPage : handledPage;
          const totalPages = isNew ? quoteRequestsTotalPages : handledTotalPages;
          const total = isNew ? quoteRequestsTotal : handledTotal;
          const setPage = isNew ? setQuoteRequestsPage : setHandledPage;
          return (
            <>
              <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-3)', flexWrap: 'wrap' }}>
                <button onClick={() => setRequestsSubView('new')} style={chipStyle(isNew)}>New{quoteRequestsTotal ? ` (${quoteRequestsTotal})` : ''}</button>
                <button onClick={() => setRequestsSubView('handled')} style={chipStyle(!isNew)}>Handled</button>
              </div>
              {rows.length === 0 ? (
                <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: 'var(--spacing-4)' }}>
                  {isNew ? 'No new website quote requests.' : 'No handled requests yet.'}
                </p>
              ) : (
                <div style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
                  {rows.map((o) => (
                    <div key={o.id} style={{ ...pageCardStyle, padding: 'var(--spacing-3) var(--spacing-4)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--spacing-3)', flexWrap: 'wrap' }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
                          <strong>{o.subject ?? 'New Website Quote Request'}</strong>
                          {isNew && (
                            <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, backgroundColor: 'var(--color-accent)', color: '#fff', borderRadius: 6, padding: '2px 8px' }}>NEW</span>
                          )}
                          {o.quoteId && (
                            <span style={{ ...badgeStyleFor(o.quoteStatus || 'DRAFT'), fontSize: 'var(--font-size-xs)' }} title="The quote created from this request">
                              {o.quoteNumber ?? 'Quote'}{o.quoteStatus ? ` · ${o.quoteStatus}` : ''}
                            </span>
                          )}
                        </div>
                        <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
                          {o.fromName ?? 'Unknown sender'}
                          {o.fromEmail ? ` · ${o.fromEmail}` : ''}
                          {o.receivedAt ? ` · Received ${bneDateTime(o.receivedAt)}` : ''}
                        </div>
                      </div>
                      <button style={primaryButtonStyle} onClick={() => setOpenQuoteRequest(o)}>Open</button>
                    </div>
                  ))}
                </div>
              )}
              {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 'var(--spacing-2)', marginTop: 'var(--spacing-3)' }}>
                  <button type="button" aria-label="Previous page" style={smallButtonStyle} disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>‹ Previous</button>
                  <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>Page {page} of {totalPages} · {total} total</span>
                  <button type="button" aria-label="Next page" style={smallButtonStyle} disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next ›</button>
                </div>
              )}
            </>
          );
        })()}

        {tab === 'unscheduled' &&
          (unscheduledLoading ? (
            <p style={{ color: 'var(--color-text-muted)' }}>Loading…</p>
          ) : (
            <>
              {/* Totals at the top */}
              <div
                style={{
                  display: 'flex',
                  gap: 'var(--spacing-6)',
                  padding: 'var(--spacing-3) var(--spacing-4)',
                  marginBottom: 'var(--spacing-3)',
                  backgroundColor: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-default)',
                }}
              >
                <div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Awaiting scheduling
                  </div>
                  <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700 }}>{filteredUnscheduled.length}</div>
                </div>
                <div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Total value
                  </div>
                  <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--color-accent)' }}>
                    {moneyTotal(filteredUnscheduled.reduce((s, c) => s + (Number(c.value) || 0), 0))}
                  </div>
                </div>
              </div>

              {/* Status filter chips (ACCEPTED quote / AUTHORISED invoice) */}
              {unschedStatuses.length > 2 && (
                <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-3)', flexWrap: 'wrap' }}>
                  {unschedStatuses.map((s) => (
                    <button key={s} onClick={() => setUnschedStatus(s)} style={chipStyle(unschedStatus === s)}>
                      {s === 'ALL' ? 'All' : s}
                    </button>
                  ))}
                </div>
              )}

              <ResponsiveList
                rows={filteredUnscheduled}
                getRowKey={(c) => c.contactId}
                emptyMessage={search ? 'No matches for your search.' : 'No customers awaiting scheduling.'}
                columns={[
                  {
                    key: 'number',
                    label: 'Number',
                    primaryOnMobile: true,
                    render: (c) =>
                      c.latestDocId ? (
                        <DocNumber
                          kind={c.latestDocType === 'invoice' ? 'invoice' : 'quote'}
                          row={{ xero_id: c.latestDocId, number: c.latestDocNumber }}
                        />
                      ) : (
                        '—'
                      ),
                  },
                  {
                    key: 'contact',
                    label: 'Contact',
                    render: (c) => (
                      <div>
                        <div>{c.contactName ?? '—'}</div>
                        {c.address && (
                          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                            {c.address}
                          </div>
                        )}
                      </div>
                    ),
                  },
                  { key: 'status', label: 'Status', render: (c) => c.status },
                  { key: 'date', label: 'Date', render: (c) => c.latestDocDate ?? '—' },
                  { key: 'value', label: 'Total', align: 'right', render: (c) => moneyTotal(Number(c.value) || 0) },
                  {
                    key: 'action',
                    label: '',
                    align: 'right',
                    render: (c) => (
                      <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() =>
                            setScheduling({
                              contactId: c.contactId,
                              name: c.contactName ?? 'customer',
                              docNumber: c.latestDocNumber,
                              address: c.address,
                              docId: c.latestDocId,
                              docType: c.latestDocType,
                            })
                          }
                          style={primaryButtonStyle}
                        >
                          Add to calendar
                        </button>
                        {c.customerId && (
                          <button
                            onClick={() => remindMutation.mutate(c.customerId as number)}
                            disabled={remindMutation.isPending}
                            style={secondaryButtonStyle}
                          >
                            Send reminder
                          </button>
                        )}
                      </div>
                    ),
                  },
                ]}
              />
            </>
          ))}

        {tab === 'scheduled' &&
          (scheduledLoading ? (
            <p style={{ color: 'var(--color-text-muted)' }}>Loading…</p>
          ) : (
            <>
              <div
                style={{
                  display: 'flex',
                  gap: 'var(--spacing-6)',
                  padding: 'var(--spacing-3) var(--spacing-4)',
                  marginBottom: 'var(--spacing-3)',
                  backgroundColor: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-default)',
                }}
              >
                <div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Scheduled jobs
                  </div>
                  <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700 }}>{filteredScheduled.length}</div>
                </div>
                <div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Total value
                  </div>
                  <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--color-accent)' }}>
                    {moneyTotal(filteredScheduled.reduce((s, j) => s + (Number(j.value) || 0), 0))}
                  </div>
                </div>
              </div>

              <ResponsiveList
                rows={filteredScheduled}
                getRowKey={(j) => j.bookingId}
                emptyMessage={search ? 'No matches for your search.' : 'No upcoming scheduled jobs.'}
                columns={[
                  {
                    key: 'number',
                    label: 'Number',
                    primaryOnMobile: true,
                    render: (j) =>
                      j.docId ? (
                        <DocNumber kind={j.docType === 'invoice' ? 'invoice' : 'quote'} row={{ xero_id: j.docId, number: j.docNumber }} />
                      ) : (
                        '—'
                      ),
                  },
                  {
                    key: 'customer',
                    label: 'Customer',
                    render: (j) => (
                      <div>
                        <div>{j.customerName ?? '—'}</div>
                        {j.address && (
                          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{j.address}</div>
                        )}
                      </div>
                    ),
                  },
                  {
                    key: 'when',
                    label: 'Scheduled',
                    render: (j) => (
                      <div>
                        <div>{bneDateTime(j.start)}</div>
                        {!j.preWashSentAt && j.daysUntil >= 0 && j.daysUntil <= 3 && (
                          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-error)', fontWeight: 600 }}>
                            Pre-wash due {j.daysUntil === 0 ? 'today' : `in ${j.daysUntil}d`}
                          </div>
                        )}
                      </div>
                    ),
                  },
                  { key: 'value', label: 'Total', align: 'right', render: (j) => moneyTotal(Number(j.value) || 0) },
                  {
                    key: 'prewash',
                    label: 'Pre-wash',
                    align: 'right',
                    render: (j) =>
                      j.preWashSentAt ? (
                        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-success)', fontWeight: 600 }}>
                          Sent ✓
                        </span>
                      ) : (
                        <button
                          style={{ ...chipStyle(false), cursor: j.customerEmail ? 'pointer' : 'not-allowed', opacity: j.customerEmail ? 1 : 0.5 }}
                          disabled={!j.customerEmail}
                          title={j.customerEmail ? 'Email the Pre-Wash Guide + arrival window' : 'No email on file'}
                          onClick={() =>
                            setPendingPreWash({ bookingId: j.bookingId, name: j.customerName ?? 'customer', email: j.customerEmail, when: bneDateTime(j.start) })
                          }
                        >
                          Send pre-wash
                        </button>
                      ),
                  },
                ]}
              />
            </>
          ))}

        {tab === 'quotes' && (
          <DocTab
            kind="quote"
            rows={quotes}
            loading={quotesLoading}
            search={search}
            onAcceptAndBook={(r) =>
              setScheduling({
                contactId: r.contact_id,
                name: r.contact_name ?? 'customer',
                docNumber: r.number,
                docId: r.xero_id,
                docType: 'quote',
                address: r.reference && !/^(qu|inv)[- ]?\d+$/i.test(String(r.reference).trim()) ? r.reference : null,
                acceptQuoteId: r.xero_id,
              })
            }
            contactIdFilter={contactFilter?.id}
            onOpenContact={openContact}
            onOpen={(r) => setOpenQuoteId(r.xero_id)}
          />
        )}

        {tab === 'invoices' && <DocTab kind="invoice" rows={invoices} loading={invoicesLoading} search={search} contactIdFilter={contactFilter?.id} onOpenContact={openContact} onOpen={(r) => setOpenInvoiceId(r.xero_id)} />}
      </section>

      {scheduling && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setScheduling(null)}
        >
          <div
            style={{ ...pageCardStyle, width: 'min(420px, 92vw)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0 }}>{scheduling.acceptQuoteId ? 'Mark accepted & book' : 'Add to calendar'}</h2>
            <p style={{ color: 'var(--color-text-muted)', marginTop: 0 }}>
              {scheduling.acceptQuoteId ? (
                <>
                  Accepts <strong>{scheduling.docNumber}</strong> in Xero, then books a job for{' '}
                  <strong>{scheduling.name}</strong> and adds it to the calendar.
                </>
              ) : (
                <>
                  Creates a booking and a Google Calendar event titled{' '}
                  <strong>
                    {scheduling.name}
                    {scheduling.docNumber ? ` — ${scheduling.docNumber}` : ''}
                  </strong>
                  .
                </>
              )}
            </p>
            <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--spacing-1)' }}>
              Date &amp; time
            </label>
            <input
              type="datetime-local"
              value={scheduleAt}
              onChange={(e) => setScheduleAt(e.target.value)}
              style={{
                width: '100%',
                minHeight: 44,
                padding: 'var(--spacing-2) var(--spacing-3)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-default)',
                marginBottom: 'var(--spacing-3)',
              }}
            />
            <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--spacing-1)' }}>
              Duration (hours)
            </label>
            <input
              type="number"
              min={0.5}
              max={40}
              step={0.5}
              value={scheduleHours}
              onChange={(e) => setScheduleHours(Math.min(40, Math.max(0.5, Number(e.target.value) || 3)))}
              style={{
                width: '100%',
                minHeight: 44,
                padding: 'var(--spacing-2) var(--spacing-3)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-default)',
                marginBottom: 'var(--spacing-1)',
              }}
            />
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', margin: '0 0 var(--spacing-3)' }}>
              Work hours are 8:00am–5:00pm, Mon–Fri. Longer jobs roll over to the next working day (weekends skipped).
            </p>
            <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--spacing-1)' }}>
              Address (calendar location)
            </label>
            <input
              type="text"
              value={scheduleAddress}
              onChange={(e) => setScheduleAddress(e.target.value)}
              placeholder={scheduling.address ? '' : 'No address on file — enter the job site address'}
              style={{
                width: '100%',
                minHeight: 44,
                padding: 'var(--spacing-2) var(--spacing-3)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-default)',
                marginBottom: 'var(--spacing-4)',
              }}
            />
            {(scheduleMutation.isError || acceptBookMutation.isError) && (
              <p style={{ color: 'var(--color-error)' }}>
                {((scheduleMutation.error || acceptBookMutation.error) as Error)?.message}
              </p>
            )}
            <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'flex-end' }}>
              <button style={secondaryButtonStyle} onClick={() => setScheduling(null)}>
                Cancel
              </button>
              {(() => {
                const pending = scheduleMutation.isPending || acceptBookMutation.isPending;
                const summary = scheduling.docNumber
                  ? `${scheduling.docType === 'invoice' ? 'Invoice' : 'Quote'} ${scheduling.docNumber} - ${scheduling.name}`
                  : scheduling.name;
                return (
                  <button
                    style={{ ...primaryButtonStyle, opacity: !scheduleAt || pending ? 0.6 : 1 }}
                    disabled={!scheduleAt || pending}
                    onClick={() => {
                      const startTime = brisbaneInputToIso(scheduleAt);
                      const location = scheduleAddress.trim() || null;
                      const durationMins = Math.round(scheduleHours * 60);
                      if (scheduling.acceptQuoteId) {
                        acceptBookMutation.mutate({
                          quoteId: scheduling.acceptQuoteId,
                          startTime,
                          durationMins,
                          summary,
                          location,
                        });
                      } else {
                        scheduleMutation.mutate({
                          contactId: scheduling.contactId,
                          startTime,
                          durationMins,
                          summary,
                          location,
                          docId: scheduling.docId ?? null,
                          docType: scheduling.docType ?? null,
                        });
                      }
                    }}
                  >
                    {pending
                      ? scheduling.acceptQuoteId
                        ? 'Accepting…'
                        : 'Scheduling…'
                      : scheduling.acceptQuoteId
                        ? 'Accept & book'
                        : 'Add to calendar'}
                  </button>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!pendingPreWash}
        title="Send pre-wash reminder?"
        message={`Email the Pre-Wash Guide and arrival window to ${pendingPreWash?.name}${pendingPreWash?.email ? ` (${pendingPreWash.email})` : ''} for ${pendingPreWash?.when}.`}
        tone="warning"
        confirmLabel={preWashMutation.isPending ? 'Sending…' : 'Send reminder'}
        onConfirm={() => pendingPreWash && preWashMutation.mutate(pendingPreWash.bookingId)}
        onCancel={() => setPendingPreWash(null)}
      />
    </div>
  );
}

// ─── Create Quote ────────────────────────────────────────────────────────────

interface QuoteScope { scope: string; area: number }
interface QuoteLine {
  itemCode: string;
  description: string;
  quantity: number;
  unitAmount: number;
  discountRate: number;
  accountCode: string;
  taxType: string;
  scopes?: QuoteScope[]; // structured scopes from the calculator (editor-only; persisted on save)
  calc?: CalcSnapshot;   // raw calculator inputs, so reopening the calculator restores them
}

const EMPTY_LINE: QuoteLine = { itemCode: '', description: '', quantity: 1, unitAmount: 0, discountRate: 0, accountCode: '200', taxType: '' };

/**
 * Guided price calculator for the quote editor. Pick a service, answer a few
 * questions, and it works out the price (ex GST) and adds it to the quote as
 * line item(s). Rules per the owner's pricing sheet: standalone minimums apply
 * (window/solar $150, pressure $200, softwash $500) but are NOT stacked when
 * services are bundled, so every amount stays editable before it's added.
 */
type CalcService = 'softwash' | 'windows' | 'pressure' | 'solar';
type CalcSnapshot = Record<string, unknown>;
interface CalcResult { unitAmount: number; scope?: string; scopes?: QuoteScope[]; descriptionIfEmpty?: string; extraLines?: QuoteLine[]; calc?: CalcSnapshot }

/** Detect which calculator fits a quote line from its service code / description. */
function detectCalcService(line: QuoteLine): CalcService | null {
  const s = `${line.itemCode} ${line.description}`.toLowerCase();
  if (/soft ?wash/.test(s)) return 'softwash';
  if (/pressure/.test(s)) return 'pressure';
  if (/window/.test(s)) return 'windows';
  if (/solar/.test(s)) return 'solar';
  return null;
}
/** Put the scope names into the description's existing "Scope:" heading (so we
 *  don't duplicate it). If the description has no "Scope:" line, add one. Only the
 *  names go in the text — areas are kept as structured data. Re-running replaces
 *  the names on that same line. */
function withScope(desc: string, scopeNames: string): string {
  if (!scopeNames) return desc || '';
  const lines = (desc || '').split('\n');
  const idx = lines.findIndex((l) => /^\s*Scope\s*:/i.test(l));
  if (idx >= 0) {
    lines[idx] = `Scope: ${scopeNames}`;
    return lines.join('\n');
  }
  return `${(desc || '').trimEnd()}\n\nScope: ${scopeNames}`.trim();
}

function PriceCalculatorModal({ onClose, onApply, initialService, initial }: { onClose: () => void; onApply: (r: CalcResult) => void; initialService?: CalcService | null; initial?: CalcSnapshot | null }) {
  // Re-hydrate every input from the line's saved calculator snapshot (if any), so
  // reopening the calculator shows what was entered before instead of blanks.
  const s = (initial ?? {}) as any;
  const [service, setService] = useState<CalcService | null>((s.service as CalcService) ?? initialService ?? null);
  const money2 = (n: number) => Math.round(n * 100) / 100;
  const mkLine = (description: string, unitAmount: number): QuoteLine => ({ ...EMPTY_LINE, description, unitAmount: money2(unitAmount) });

  // ── Softwash ──
  const [swType, setSwType] = useState<'residential' | 'commercial'>(s.swType ?? 'residential');
  const [swStoreys, setSwStoreys] = useState<1 | 2 | 3>(s.swStoreys ?? 1);
  const [swPerimeter, setSwPerimeter] = useState<string>(s.swPerimeter ?? '');
  const [swScissor, setSwScissor] = useState<boolean>(s.swScissor ?? false);
  const [swScissorCost, setSwScissorCost] = useState<string>(s.swScissorCost ?? '');
  const swRate = swType === 'commercial' ? 67 : swStoreys === 1 ? 11 : swStoreys === 2 ? 17 : 51;
  const swRaw = (Number(swPerimeter) || 0) * swRate;
  const swMin = 500;
  const [swFinal, setSwFinal] = useState<string>(s.swFinal ?? '');
  const swPreset = swRaw > 0 ? String(money2(Math.max(swRaw, swMin))) : '';

  // ── Windows ──
  const [winApproach, setWinApproach] = useState<'panel' | 'bracket'>(s.winApproach ?? 'panel');
  const [winSliding, setWinSliding] = useState<string>(s.winSliding ?? '');
  const [winStandard, setWinStandard] = useState<string>(s.winStandard ?? '');
  const [winBalustrade, setWinBalustrade] = useState<string>(s.winBalustrade ?? '');
  const [winHouseWash, setWinHouseWash] = useState<boolean>(s.winHouseWash ?? false);
  const [winExtScreens, setWinExtScreens] = useState<string>(s.winExtScreens ?? '');
  const [winIntScreens, setWinIntScreens] = useState<string>(s.winIntScreens ?? '');
  const [winBedrooms, setWinBedrooms] = useState<3 | 4 | 5>(s.winBedrooms ?? 3);
  const [winStoreys, setWinStoreys] = useState<1 | 2>(s.winStoreys ?? 1);
  const [winBracketPrice, setWinBracketPrice] = useState<string>(s.winBracketPrice ?? '');
  const winMin = 150;
  const winPanelTotal = (Number(winSliding) || 0) * 20 + (Number(winStandard) || 0) * 10 + (Number(winBalustrade) || 0) * 14;
  const winScreensTotal = (winHouseWash ? 0 : (Number(winExtScreens) || 0) * 4) + (Number(winIntScreens) || 0) * 4;
  const [winFinal, setWinFinal] = useState<string>(s.winFinal ?? '');
  const winPanelPreset = winPanelTotal > 0 ? String(money2(Math.max(winPanelTotal, winMin))) : '';
  const BRACKETS: Record<string, Record<number, string>> = {
    '3': { 1: '$420 - $450', 2: '$600 - $680' },
    '4': { 1: '$500 - $600', 2: '$660 - $950' },
    '5': { 1: '$620 - $650', 2: '$850 - $1,100' },
  };

  // ── Pressure (one row per scope, e.g. driveway / paths / pool surrounds) ──
  const [prRows, setPrRows] = useState<{ scope: string; area: string }[]>(
    Array.isArray(s.prRows) && s.prRows.length ? s.prRows : [{ scope: '', area: '' }],
  );
  const prRaw = prRows.reduce((n, r) => n + (Number(r.area) || 0) * 3, 0);
  const prMin = 200;
  const [prFinal, setPrFinal] = useState<string>(s.prFinal ?? '');
  const prPreset = prRaw > 0 ? String(money2(Math.max(prRaw, prMin))) : '';
  const setPrRow = (i: number, patch: Partial<{ scope: string; area: string }>) => setPrRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const prEntries = prRows
    .filter((r) => r.scope.trim() || Number(r.area) > 0)
    .map((r) => ({ scope: r.scope.trim() || 'area', area: Number(r.area) || 0 }));
  // Names only go into the description ("Scope: driveway, patio"); areas are kept
  // as structured data on the line.
  const prScopeNames = prEntries.map((e) => e.scope).join(', ');

  // ── Solar ──
  const [solPanels, setSolPanels] = useState<string>(s.solPanels ?? '');
  const solRaw = (Number(solPanels) || 0) * 14;
  const solMin = 150;
  const [solFinal, setSolFinal] = useState<string>(s.solFinal ?? '');
  const solPreset = solRaw > 0 ? String(money2(Math.max(solRaw, solMin))) : '';

  // One snapshot of every input, stored on the line so a later "Calculate" reopens
  // with these values instead of blanks.
  const snapshot = (): CalcSnapshot => ({
    service, swType, swStoreys, swPerimeter, swScissor, swScissorCost, swFinal,
    winApproach, winSliding, winStandard, winBalustrade, winHouseWash, winExtScreens,
    winIntScreens, winBedrooms, winStoreys, winBracketPrice, winFinal,
    prRows, prFinal, solPanels, solFinal,
  });

  const fld: React.CSSProperties = { ...searchInputStyle, minHeight: 36, width: '100%', maxWidth: 'none', marginBottom: 0, boxSizing: 'border-box' };
  const seg = (active: boolean): React.CSSProperties => ({ ...secondaryButtonStyle, minHeight: 34, background: active ? 'var(--color-accent)' : 'transparent', color: active ? '#fff' : 'var(--color-text)', borderColor: active ? 'var(--color-accent)' : 'var(--color-border)' });
  const note: React.CSSProperties = { fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 4 };
  const minNote = (raw: number, min: number) => raw > 0 && raw < min
    ? <div style={{ ...note, color: 'var(--color-gold,#B8860B)' }}>Below the ${min} standalone minimum, pre-filled to ${min}. Lower it if bundled with other services.</div>
    : <div style={note}>Standalone minimum ${min} (not applied when bundled).</div>;

  function applySoftwash() {
    const label = swType === 'commercial' ? 'Commercial' : `Residential ${swStoreys}-storey`;
    const extraLines = swScissor && Number(swScissorCost) > 0
      ? [mkLine('Scissor lift hire (Kennards, delivery + equipment waiver)', Number(swScissorCost))]
      : undefined;
    onApply({ unitAmount: Number(swFinal || swPreset) || 0, descriptionIfEmpty: `External House Softwash (${label}, ${Number(swPerimeter) || 0} lm @ $${swRate}/lm)`, extraLines, calc: snapshot() });
  }
  function applyWindows() {
    if (winApproach === 'panel') {
      const parts = [
        Number(winSliding) ? `${winSliding} sliding` : '',
        Number(winStandard) ? `${winStandard} standard` : '',
        Number(winBalustrade) ? `${winBalustrade} balustrade` : '',
      ].filter(Boolean).join(', ');
      const extraLines = winScreensTotal > 0
        ? [mkLine(`Screen Cleaning (${[!winHouseWash && Number(winExtScreens) ? `${winExtScreens} external` : '', Number(winIntScreens) ? `${winIntScreens} internal` : ''].filter(Boolean).join(' + ')} @ $4)`, winScreensTotal)]
        : undefined;
      onApply({ unitAmount: Number(winFinal || winPanelPreset) || 0, descriptionIfEmpty: `Window Cleaning (per-panel: ${parts || 'panels'})`, extraLines, calc: snapshot() });
    } else {
      onApply({ unitAmount: Number(winBracketPrice) || 0, descriptionIfEmpty: `Window Cleaning (${winBedrooms} bedroom, ${winStoreys}-storey)`, calc: snapshot() });
    }
  }
  function applyPressure() { onApply({ unitAmount: Number(prFinal || prPreset) || 0, scope: prScopeNames, scopes: prEntries, descriptionIfEmpty: 'Pressure Cleaning', calc: snapshot() }); }
  function applySolar() { onApply({ unitAmount: Number(solFinal || solPreset) || 0, descriptionIfEmpty: `Solar Panel Cleaning (${Number(solPanels) || 0} panels @ $14)`, calc: snapshot() }); }

  const SERVICES: { key: CalcService; label: string }[] = [
    { key: 'softwash', label: 'External House Softwash' },
    { key: 'windows', label: 'Window Cleaning' },
    { key: 'pressure', label: 'Pressure Cleaning' },
    { key: 'solar', label: 'Solar Panel Cleaning' },
  ];

  return (
    <div role="dialog" aria-modal="true" onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1100, padding: 'var(--spacing-2)', overflowY: 'auto' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...pageCardStyle, width: '100%', maxWidth: 560, marginTop: 'var(--spacing-3)', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Price calculator</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--color-text-muted)' }}>✕</button>
        </div>
        <p style={{ ...note, marginTop: 4 }}>Prices are ex GST (Xero adds 10%). Every amount is editable before you add it.</p>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 'var(--spacing-2)' }}>
          {SERVICES.map((s) => (
            <button key={s.key} style={seg(service === s.key)} onClick={() => setService(s.key)}>{s.label}</button>
          ))}
        </div>

        {service === 'softwash' && (
          <div style={{ display: 'grid', gap: 8, marginTop: 'var(--spacing-3)' }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={seg(swType === 'residential')} onClick={() => setSwType('residential')}>Residential</button>
              <button style={seg(swType === 'commercial')} onClick={() => setSwType('commercial')}>Commercial</button>
            </div>
            {swType === 'residential' && (
              <div><label style={labelStyle}>Storeys</label>
                <div style={{ display: 'flex', gap: 8 }}>{([1, 2, 3] as const).map((n) => <button key={n} style={seg(swStoreys === n)} onClick={() => setSwStoreys(n)}>{n}</button>)}</div>
                <div style={note}>Use the 3-storey rate for steep blocks, restricted access, or extension-pole jobs.</div>
              </div>
            )}
            <div><label style={labelStyle}>Total linear perimeter (m)</label><input type="number" min={0} style={fld} value={swPerimeter} onChange={(e) => setSwPerimeter(e.target.value)} /></div>
            <div style={{ fontSize: 'var(--font-size-sm)' }}>Rate ${swRate}/lm · calculated <strong>${money2(swRaw).toFixed(2)}</strong></div>
            <div><label style={labelStyle}>Price to quote (ex GST)</label><input type="number" min={0} style={fld} value={swFinal || swPreset} onChange={(e) => setSwFinal(e.target.value)} />{minNote(swRaw, swMin)}</div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--font-size-sm)' }}><input type="checkbox" checked={swScissor} onChange={(e) => setSwScissor(e.target.checked)} /> Add scissor lift hire (separate line)</label>
            {swScissor && <input type="number" min={0} style={fld} placeholder="Scissor lift cost (ex GST)" value={swScissorCost} onChange={(e) => setSwScissorCost(e.target.value)} />}
            <button style={{ ...primaryButtonStyle, opacity: swRaw > 0 ? 1 : 0.5 }} disabled={swRaw <= 0} onClick={applySoftwash}>Apply to line</button>
          </div>
        )}

        {service === 'windows' && (
          <div style={{ display: 'grid', gap: 8, marginTop: 'var(--spacing-3)' }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={seg(winApproach === 'panel')} onClick={() => setWinApproach('panel')}>Per-panel</button>
              <button style={seg(winApproach === 'bracket')} onClick={() => setWinApproach('bracket')}>Whole-house bracket</button>
            </div>
            {winApproach === 'panel' ? (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                  <div><label style={labelStyle}>Sliding ($20)</label><input type="number" min={0} style={fld} value={winSliding} onChange={(e) => setWinSliding(e.target.value)} /></div>
                  <div><label style={labelStyle}>Standard ($10)</label><input type="number" min={0} style={fld} value={winStandard} onChange={(e) => setWinStandard(e.target.value)} /></div>
                  <div><label style={labelStyle}>Balustrade ($14)</label><input type="number" min={0} style={fld} value={winBalustrade} onChange={(e) => setWinBalustrade(e.target.value)} /></div>
                </div>
                <div style={{ fontSize: 'var(--font-size-sm)' }}>Panels calculated <strong>${money2(winPanelTotal).toFixed(2)}</strong></div>
                <div><label style={labelStyle}>Window price to quote (ex GST)</label><input type="number" min={0} style={fld} value={winFinal || winPanelPreset} onChange={(e) => setWinFinal(e.target.value)} />{minNote(winPanelTotal, winMin)}</div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--font-size-sm)' }}><input type="checkbox" checked={winHouseWash} onChange={(e) => setWinHouseWash(e.target.checked)} /> House wash included (external screens free)</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div><label style={labelStyle}>External screens</label><input type="number" min={0} style={fld} value={winExtScreens} onChange={(e) => setWinExtScreens(e.target.value)} /></div>
                  <div><label style={labelStyle}>Internal screens</label><input type="number" min={0} style={fld} value={winIntScreens} onChange={(e) => setWinIntScreens(e.target.value)} /></div>
                </div>
                <div style={note}>Screens $4 each. External free with a house wash; internal always charged. Screens added as a separate line (${money2(winScreensTotal).toFixed(2)}).</div>
              </>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div><label style={labelStyle}>Bedrooms</label><div style={{ display: 'flex', gap: 6 }}>{([3, 4, 5] as const).map((n) => <button key={n} style={seg(winBedrooms === n)} onClick={() => setWinBedrooms(n)}>{n}</button>)}</div></div>
                  <div><label style={labelStyle}>Storeys</label><div style={{ display: 'flex', gap: 6 }}>{([1, 2] as const).map((n) => <button key={n} style={seg(winStoreys === n)} onClick={() => setWinStoreys(n)}>{n}</button>)}</div></div>
                </div>
                <div style={{ fontSize: 'var(--font-size-sm)' }}>Bracket guide: <strong>{BRACKETS[String(winBedrooms)][winStoreys]}</strong></div>
                <div><label style={labelStyle}>Price to quote (ex GST)</label><input type="number" min={0} style={fld} value={winBracketPrice} onChange={(e) => setWinBracketPrice(e.target.value)} placeholder="Pick a figure in the range" /></div>
              </>
            )}
            <div style={{ ...note, borderTop: '1px solid var(--color-border)', paddingTop: 6 }}>
              Surcharge triggers (confirm before pricing): louvres / louvres at height, colonial multi-pane, fixed flyscreens or crimsafe needing glass lift-out, or 5+ room combos (these typically run $1,200 to $1,500).
            </div>
            <button style={primaryButtonStyle} onClick={applyWindows}>Apply to line</button>
          </div>
        )}

        {service === 'pressure' && (
          <div style={{ display: 'grid', gap: 8, marginTop: 'var(--spacing-3)' }}>
            <label style={labelStyle}>Scopes ($3/m² each)</label>
            {prRows.map((r, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 92px 84px 32px', gap: 8, alignItems: 'center' }}>
                <input style={fld} placeholder="e.g. concrete driveway" value={r.scope} onChange={(e) => setPrRow(i, { scope: e.target.value })} />
                <input type="number" min={0} style={fld} placeholder="Area m²" value={r.area} onChange={(e) => setPrRow(i, { area: e.target.value })} />
                <div style={{ textAlign: 'right', fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>${money2((Number(r.area) || 0) * 3).toFixed(2)}</div>
                <button title="Remove scope" style={{ ...secondaryButtonStyle, minHeight: 30, padding: '2px 8px' }} disabled={prRows.length <= 1} onClick={() => setPrRows((prev) => prev.filter((_, j) => j !== i))}>✕</button>
              </div>
            ))}
            <button style={{ ...secondaryButtonStyle, justifySelf: 'start' }} onClick={() => setPrRows((prev) => [...prev, { scope: '', area: '' }])}>+ Add scope</button>
            <div style={{ fontSize: 'var(--font-size-sm)' }}>Total area {prRows.reduce((n, r) => n + (Number(r.area) || 0), 0)} m² · calculated <strong>${money2(prRaw).toFixed(2)}</strong></div>
            <div><label style={labelStyle}>Price to quote (ex GST)</label><input type="number" min={0} style={fld} value={prFinal || prPreset} onChange={(e) => setPrFinal(e.target.value)} />{minNote(prRaw, prMin)}</div>
            <div style={note}>Added to the description as “Scope: {prScopeNames || 'driveway, patio'}”, and the areas are saved with the quote.</div>
            <button style={{ ...primaryButtonStyle, opacity: prRaw > 0 ? 1 : 0.5 }} disabled={prRaw <= 0} onClick={applyPressure}>Apply to line</button>
          </div>
        )}

        {service === 'solar' && (
          <div style={{ display: 'grid', gap: 8, marginTop: 'var(--spacing-3)' }}>
            <div><label style={labelStyle}>Number of panels</label><input type="number" min={0} style={fld} value={solPanels} onChange={(e) => setSolPanels(e.target.value)} /></div>
            <div style={{ fontSize: 'var(--font-size-sm)' }}>Rate $14/panel · calculated <strong>${money2(solRaw).toFixed(2)}</strong></div>
            <div><label style={labelStyle}>Price to quote (ex GST)</label><input type="number" min={0} style={fld} value={solFinal || solPreset} onChange={(e) => setSolFinal(e.target.value)} />{minNote(solRaw, solMin)}</div>
            <button style={{ ...primaryButtonStyle, opacity: solRaw > 0 ? 1 : 0.5 }} disabled={solRaw <= 0} onClick={applySolar}>Apply to line</button>
          </div>
        )}
      </div>
    </div>
  );
}

function isoPlusDays(days: number): string {
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Australia/Brisbane' });
  const d = new Date(`${today}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString('en-CA');
}

/** Create a quote in Xero: pick a local contact, add service line items from the
 *  Xero catalogue, then create it (SENT). Creates the Xero contact if missing. */
function CreateQuoteModal({ onClose, onCreated, fromRequest, observationId }: { onClose: () => void; onCreated: (quoteId?: string | null) => void; fromRequest?: QuoteRequestData | null; observationId?: number }) {
  const [contactSearch, setContactSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [contact, setContact] = useState<{ id: number; name: string } | null>(null);
  const [issueDate, setIssueDate] = useState(isoPlusDays(0));
  const [expiryDate, setExpiryDate] = useState(isoPlusDays(14));
  const [reference, setReference] = useState('');
  const [rows, setRows] = useState<QuoteLine[]>([{ ...EMPTY_LINE }]);
  const [calcLineIdx, setCalcLineIdx] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ quoteNumber: string | null; quoteId: string | null } | null>(null);
  // Apply the calculator's result to the line it was opened from: set the price,
  // fill the description only if empty (keeps the service template), add the scope
  // for pressure, and append any extra lines (e.g. scissor lift, screens).
  const applyCalcToLine = (res: CalcResult) => {
    setRows((prev) => {
      if (calcLineIdx == null) return prev;
      const mapped = prev.map((row, j) => {
        if (j !== calcLineIdx) return row;
        let description = row.description;
        if (!description.trim() && res.descriptionIfEmpty) description = res.descriptionIfEmpty;
        if (res.scope) description = withScope(description, res.scope);
        return { ...row, unitAmount: res.unitAmount, description, ...(res.scopes ? { scopes: res.scopes } : {}), ...(res.calc ? { calc: res.calc } : {}) };
      });
      return [...mapped, ...(res.extraLines ?? [])];
    });
    setCalcLineIdx(null);
  };
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 640);
  const [showNew, setShowNew] = useState(false);
  const [nc, setNc] = useState({ full_name: '', email: '', mobile: '' });
  const [ncError, setNcError] = useState<string | null>(null);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 640);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(contactSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [contactSearch]);

  // Pre-fill from a website quote request: reuse (by email/mobile) or create the
  // customer, and set the property address as the reference.
  useEffect(() => {
    if (!fromRequest) return;
    let cancelled = false;
    (async () => {
      try {
        const c = await api.resolveOrCreateCustomer({ name: fromRequest.contactName, email: fromRequest.contactEmail, mobile: fromRequest.contactPhone });
        if (!cancelled && c?.id) setContact({ id: c.id, name: fromRequest.contactName || fromRequest.contactEmail || `Contact #${c.id}` });
      } catch { /* leave contact empty for a manual pick */ }
      if (cancelled) return;
      if (fromRequest.address) setReference(fromRequest.address);
    })();
    return () => { cancelled = true; };
  }, [fromRequest]);

  const createContactMut = useMutation({
    mutationFn: () => api.createContact({ full_name: nc.full_name.trim(), email: nc.email.trim() || null, mobile: nc.mobile.trim() || null }),
    onSuccess: (c: any) => {
      setContact({ id: c.id, name: c.full_name || nc.full_name.trim() });
      setShowNew(false);
      setNc({ full_name: '', email: '', mobile: '' });
      setNcError(null);
    },
    onError: (e: any) => setNcError(e?.message || 'Could not create the contact.'),
  });
  const canCreateContact = nc.full_name.trim() && (nc.email.trim() || nc.mobile.trim()) && !createContactMut.isPending;

  const { data: contactResults } = useQuery({
    queryKey: ['quote-contact-search', debounced],
    queryFn: () => api.getContacts({ search: debounced, limit: 8 }),
    enabled: !contact && debounced.length >= 2,
  });
  const { data: items = [], isLoading: itemsLoading, isError: itemsError } = useQuery({
    queryKey: ['xero-items'],
    queryFn: () => api.getXeroItems(),
  });

  // Add one line per requested service, with the matching Xero service SELECTED
  // (so the description auto-fills, exactly like picking it from the dropdown).
  // Waits for the items list; runs once. Falls back to the label if no item matches.
  const rowsPrefilled = useRef(false);
  useEffect(() => {
    if (!fromRequest || rowsPrefilled.current) return;
    const svcs = fromRequest.services ?? [];
    if (!svcs.length || itemsLoading) return;
    rowsPrefilled.current = true;
    const findItem = (name: string) => {
      const n = name.trim().toLowerCase();
      return (
        (items as any[]).find((x) => (x.name || '').trim().toLowerCase() === n) ??
        (items as any[]).find((x) => (x.name || '').toLowerCase().includes(n))
      );
    };
    // Answers the customer gave for a service (matched by keyword on the answer key).
    const multi = fromRequest.serviceMultiAnswers ?? {};
    const answersFor = (kw: RegExp): string[] => Object.entries(multi).filter(([k]) => kw.test(k)).flatMap(([, v]) => v);
    const floorsNum = Math.max(1, Math.min(3, parseInt(String(fromRequest.floors ?? '1'), 10) || 1));

    // Map each requested service to a Xero item (owner's mapping) + carry the
    // customer's answers into the price calculator (storeys, pressure scope).
    const built = svcs.map((s): QuoteLine => {
      const low = s.toLowerCase();
      let itemName: string | null = null;
      let calc: CalcSnapshot | undefined;
      let scopes: QuoteScope[] | undefined;
      let scopeNames = '';

      if (/soft\s*wash|house\s*wash/.test(low)) {
        itemName = 'External Building Softwash';
        calc = { service: 'softwash', swStoreys: floorsNum }; // carry storeys into the softwash calc
      } else if (/window/.test(low)) {
        const ans = answersFor(/window/i).join(' ').toLowerCase();
        itemName = /internal/.test(ans) ? 'Internal & External Window Cleaning' : 'External Window Cleaning';
        calc = { service: 'windows', winStoreys: Math.min(2, floorsNum) };
      } else if (/solar/.test(low)) {
        itemName = 'Solar Panel Cleaning';
      } else if (/pressure/.test(low)) {
        itemName = 'Pressure Cleaning';
        const scopeItems = answersFor(/pressure/i); // e.g. ["Driveway", "Footpath"]
        if (scopeItems.length) {
          scopes = scopeItems.map((x) => ({ scope: x, area: 0 }));
          scopeNames = scopeItems.join(', ');
          calc = { service: 'pressure', prRows: scopeItems.map((x) => ({ scope: x, area: '' })) }; // carry scope into the pressure calc
        }
      } else if (/awning/.test(low)) {
        itemName = 'External Window Cleaning';
      }

      const it = itemName ? findItem(itemName) : null;
      if (it) {
        let description = it.description || it.name || s;
        if (scopeNames) description = withScope(description, scopeNames);
        return {
          ...EMPTY_LINE,
          itemCode: it.code,
          description,
          unitAmount: typeof it.unitPrice === 'number' ? it.unitPrice : 0,
          accountCode: it.accountCode || '200',
          taxType: it.taxType || '',
          ...(scopes ? { scopes } : {}),
          ...(calc ? { calc } : {}),
        };
      }
      return { ...EMPTY_LINE, description: s }; // no mapping/item — keep the label
    });
    if (built.length) setRows(built);
  }, [fromRequest, items, itemsLoading]);

  // On selecting a contact, prefill the reference with their address.
  async function pickContact(c: any) {
    setContact({ id: c.id, name: c.fullName || c.email || `Contact #${c.id}` });
    setContactSearch('');
    try {
      const full = await api.getCustomerRecord(c.id);
      const cu = full?.customer || {};
      const addr = [cu.address_1, cu.suburb, cu.state, cu.postcode].filter(Boolean).join(', ');
      if (addr) setReference(addr);
    } catch { /* leave reference blank */ }
  }

  function setRow(i: number, patch: Partial<QuoteLine>) {
    setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  }
  function onPickItem(i: number, code: string) {
    const it = (items as any[]).find((x) => x.code === code);
    if (!it) { setRow(i, { itemCode: '' }); return; }
    setRow(i, {
      itemCode: it.code,
      description: it.description || it.name || '',
      unitAmount: typeof it.unitPrice === 'number' ? it.unitPrice : 0,
      accountCode: it.accountCode || '200',
      taxType: it.taxType || '',
    });
  }

  const subtotal = rows.reduce((n, r) => n + r.quantity * r.unitAmount * (1 - (r.discountRate || 0) / 100), 0);

  const create = useMutation({
    mutationFn: async () => {
      const res = await api.createQuote({
        customerId: contact!.id,
        issueDate,
        expiryDate,
        reference: reference || undefined,
        observationId: observationId || undefined, // links the quote to the website request it came from
        lineItems: rows
          .filter((r) => r.description.trim())
          .map((r) => ({
            itemCode: r.itemCode || null,
            description: r.description,
            quantity: r.quantity,
            unitAmount: r.unitAmount,
            accountCode: r.accountCode || '200',
            taxType: r.taxType || null,
            discountRate: r.discountRate || null,
          })),
      });
      // Persist the calculator scopes + inputs against the new quote (best effort).
      // Line calc is keyed by index into the SAME filtered lines sent to Xero.
      const kept = rows.filter((r) => r.description.trim());
      const scopes = rows.flatMap((r) => (r.scopes ?? []).map((s) => ({ service: r.itemCode || null, scope: s.scope, areaM2: s.area })));
      if ((res as any)?.quoteId && scopes.length) {
        try { await api.saveQuoteScopes((res as any).quoteId, { quoteNumber: res.quoteNumber, scopes }); } catch { /* non-fatal */ }
      }
      const calcs = kept.map((r, i) => (r.calc ? { lineIndex: i, calc: r.calc } : null)).filter(Boolean) as { lineIndex: number; calc: any }[];
      if ((res as any)?.quoteId && calcs.length) {
        try { await api.saveQuoteLineCalc((res as any).quoteId, calcs); } catch { /* non-fatal */ }
      }
      return res;
    },
    onSuccess: (r) => {
      const id = (r as any).quoteId ?? null;
      // Drop the user straight into the created quote's editable view — no
      // intermediate "created, click to open" screen. Fall back to the little
      // confirmation card only if Xero didn't return an id to open.
      if (id) onCreated(id);
      else setDone({ quoteNumber: r.quoteNumber, quoteId: null });
    },
    onError: (e: any) => setError(e?.message || 'Could not create the quote.'),
  });

  const canCreate = Boolean(contact) && Boolean(issueDate) && Boolean(reference.trim()) && rows.some((r) => r.description.trim() && r.quantity > 0) && !create.isPending;

  // Shared line-item grid: Service | Description | Qty | Price | Disc% | Amount | ✕
  const LINE_COLS = '200px minmax(240px, 1fr) 72px 110px 84px 110px 128px';
  // Note: searchInputStyle caps width at 360px and adds a bottom margin — override
  // both so inputs fill their grid cell (the description textarea especially).
  const quoteInput: React.CSSProperties = { ...searchInputStyle, minHeight: 38, width: '100%', maxWidth: 'none', marginBottom: 0, boxSizing: 'border-box' };

  return (
    <div role="dialog" aria-modal="true" onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'stretch', justifyContent: 'center', zIndex: 1000, padding: 'var(--spacing-2)' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...pageCardStyle, width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%', overflowY: 'auto', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>New quote</h2>
          <button style={secondaryButtonStyle} onClick={onClose}>Close</button>
        </div>

        {done ? (
          <div style={{ padding: 'var(--spacing-4) 0' }}>
            <p style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, color: 'var(--color-success)' }}>
              ✓ Quote {done.quoteNumber ? `${done.quoteNumber} ` : ''}created in Xero for {contact?.name}.
            </p>
            <p style={{ color: 'var(--color-text-muted)' }}>It’s saved as a Draft in Xero. Open it to keep editing, add services or use the price calculator.</p>
            <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
              <button style={primaryButtonStyle} onClick={() => onCreated(done.quoteId)}>Open the quote →</button>
              <button style={secondaryButtonStyle} onClick={() => onCreated(null)}>Back to quotes</button>
            </div>
          </div>
        ) : (
          <>
            {/* Contact + dates + reference */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 'var(--spacing-3)', marginTop: 'var(--spacing-3)' }}>
              <div>
                <label style={labelStyle}>Contact <span style={{ color: 'var(--color-error)' }}>*</span></label>
                {contact ? (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <strong>{contact.name}</strong>
                    <button style={{ ...secondaryButtonStyle, minHeight: 28, padding: '2px 8px', fontSize: 'var(--font-size-xs)' }} onClick={() => setContact(null)}>Change</button>
                  </div>
                ) : showNew ? (
                  <div style={{ display: 'grid', gap: 6 }}>
                    <input style={searchInputStyle} placeholder="Full name *" value={nc.full_name} onChange={(e) => { setNc((p) => ({ ...p, full_name: e.target.value })); setNcError(null); }} />
                    <input style={searchInputStyle} placeholder="Email" type="email" value={nc.email} onChange={(e) => { setNc((p) => ({ ...p, email: e.target.value })); setNcError(null); }} />
                    <input style={searchInputStyle} placeholder="Mobile" value={nc.mobile} onChange={(e) => { setNc((p) => ({ ...p, mobile: e.target.value })); setNcError(null); }} />
                    {ncError && <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-xs)' }}>{ncError}</span>}
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button style={{ ...primaryButtonStyle, minHeight: 34, opacity: canCreateContact ? 1 : 0.6 }} disabled={!canCreateContact} onClick={() => createContactMut.mutate()}>
                        {createContactMut.isPending ? 'Creating…' : 'Create & use'}
                      </button>
                      <button style={{ ...secondaryButtonStyle, minHeight: 34 }} onClick={() => { setShowNew(false); setNcError(null); }}>Cancel</button>
                    </div>
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Needs a name and an email or mobile.</span>
                  </div>
                ) : (
                  <>
                    <input style={searchInputStyle} placeholder="Search contacts…" value={contactSearch} onChange={(e) => setContactSearch(e.target.value)} />
                    {(contactResults?.contacts?.length ?? 0) > 0 && (
                      <div style={{ border: '1px solid var(--color-border)', borderRadius: 6, marginTop: 4, maxHeight: 180, overflowY: 'auto' }}>
                        {contactResults.contacts.map((c: any) => (
                          <button key={c.id} onClick={() => pickContact(c)} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px', background: 'none', border: 'none', borderBottom: '1px solid var(--color-border)', cursor: 'pointer' }}>
                            <strong>{c.fullName || '(no name)'}</strong>
                            <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}> {[c.email, c.mobile].filter(Boolean).join(' · ')}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    <button style={{ ...secondaryButtonStyle, minHeight: 30, marginTop: 6, fontSize: 'var(--font-size-xs)' }} onClick={() => { setShowNew(true); if (contactSearch.trim() && !contactSearch.includes('@')) setNc((p) => ({ ...p, full_name: contactSearch.trim() })); }}>
                      + New contact
                    </button>
                  </>
                )}
              </div>
              <div>
                <label style={labelStyle}>Issue date</label>
                <input type="date" style={searchInputStyle} value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Expiration date</label>
                <input type="date" style={searchInputStyle} value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Property address <span style={{ color: 'var(--color-error)' }}>*</span></label>
                <input
                  style={{ ...searchInputStyle, ...(reference.trim() ? {} : { border: '1px solid var(--color-error)' }) }}
                  placeholder="Property address"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                />
              </div>
            </div>

            {/* Line items */}
            <div style={{ marginTop: 'var(--spacing-4)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 'var(--spacing-2)', flexWrap: 'wrap', gap: '4px 12px' }}>
                <h3 style={{ margin: 0, fontSize: 'var(--font-size-md)' }}>Services</h3>
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Account 200 – Sales and GST are applied automatically (as in Xero).</span>
              </div>
              {itemsError && <p style={{ color: 'var(--color-error)' }}>Couldn’t load Xero items — check the Xero connection.</p>}
              {isMobile ? (
                /* Mobile: each service is a stacked card — no horizontal scroll. */
                <div style={{ display: 'grid', gap: 'var(--spacing-3)' }}>
                  {rows.map((r, i) => {
                    const amount = r.quantity * r.unitAmount * (1 - (r.discountRate || 0) / 100);
                    return (
                      <div key={i} style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: 'var(--spacing-3)', display: 'grid', gap: 8 }}>
                        <div>
                          <label style={labelStyle}>Service</label>
                          <select style={{ ...quoteInput, height: 40 }} value={r.itemCode} onChange={(e) => onPickItem(i, e.target.value)} disabled={itemsLoading}>
                            <option value="">{itemsLoading ? 'Loading…' : 'Select…'}</option>
                            {(items as any[]).map((it) => (<option key={it.code} value={it.code}>{it.code} — {it.name}</option>))}
                          </select>
                        </div>
                        <div>
                          <label style={labelStyle}>Description</label>
                          <textarea style={{ ...quoteInput, minHeight: 120, resize: 'vertical', lineHeight: 1.4 }} placeholder="Description" value={r.description} onChange={(e) => setRow(i, { description: e.target.value })} />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                          <div><label style={labelStyle}>Qty</label><input type="number" min={0} style={{ ...quoteInput, height: 40 }} value={r.quantity} onChange={(e) => setRow(i, { quantity: Number(e.target.value) })} /></div>
                          <div><label style={labelStyle}>Price</label><input type="number" step="0.01" min={0} style={{ ...quoteInput, height: 40 }} value={r.unitAmount} onChange={(e) => setRow(i, { unitAmount: Number(e.target.value) })} /></div>
                          <div><label style={labelStyle}>Disc %</label><input type="number" min={0} max={100} style={{ ...quoteInput, height: 40 }} value={r.discountRate} onChange={(e) => setRow(i, { discountRate: Number(e.target.value) })} /></div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontWeight: 600 }}>Amount: ${amount.toFixed(2)}</span>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button title="Work out the price for this service" style={{ ...secondaryButtonStyle, minHeight: 32 }} onClick={() => setCalcLineIdx(i)}>💲 Calculate</button>
                            {rows.length > 1 && (
                              <button title="Remove service" style={{ ...secondaryButtonStyle, minHeight: 32, color: 'var(--color-error)' }} onClick={() => setRows((prev) => prev.filter((_, j) => j !== i))}>Remove</button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <div style={{ minWidth: 720 }}>
                    {/* Header */}
                    <div style={{ display: 'grid', gridTemplateColumns: LINE_COLS, gap: 'var(--spacing-2)', padding: '0 4px var(--spacing-1)', borderBottom: '1px solid var(--color-border)' }}>
                      {['Service', 'Description', 'Qty', 'Price', 'Disc %', 'Amount', ''].map((h, i) => (
                        <div key={i} style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', textAlign: i >= 2 && i <= 5 ? 'right' : 'left' }}>{h}</div>
                      ))}
                    </div>
                    {/* Rows */}
                    {rows.map((r, i) => {
                      const amount = r.quantity * r.unitAmount * (1 - (r.discountRate || 0) / 100);
                      return (
                        <div key={i} style={{ display: 'grid', gridTemplateColumns: LINE_COLS, gap: 'var(--spacing-2)', alignItems: 'start', padding: 'var(--spacing-2) 4px', borderBottom: '1px solid var(--color-border)' }}>
                          <select style={{ ...quoteInput, height: 38 }} value={r.itemCode} onChange={(e) => onPickItem(i, e.target.value)} disabled={itemsLoading}>
                            <option value="">{itemsLoading ? 'Loading…' : 'Select…'}</option>
                            {(items as any[]).map((it) => (
                              <option key={it.code} value={it.code}>{it.code} — {it.name}</option>
                            ))}
                          </select>
                          <textarea style={{ ...quoteInput, minHeight: 120, resize: 'vertical', lineHeight: 1.4 }} placeholder="Description" value={r.description} onChange={(e) => setRow(i, { description: e.target.value })} />
                          <input type="number" min={0} style={{ ...quoteInput, height: 38, textAlign: 'right' }} value={r.quantity} onChange={(e) => setRow(i, { quantity: Number(e.target.value) })} />
                          <input type="number" step="0.01" min={0} style={{ ...quoteInput, height: 38, textAlign: 'right' }} value={r.unitAmount} onChange={(e) => setRow(i, { unitAmount: Number(e.target.value) })} />
                          <input type="number" min={0} max={100} style={{ ...quoteInput, height: 38, textAlign: 'right' }} value={r.discountRate} onChange={(e) => setRow(i, { discountRate: Number(e.target.value) })} />
                          <div style={{ textAlign: 'right', paddingTop: 9, fontWeight: 600, whiteSpace: 'nowrap' }}>${amount.toFixed(2)}</div>
                          <div style={{ paddingTop: 4, display: 'flex', gap: 4 }}>
                            <button title="Work out the price for this service" style={{ ...secondaryButtonStyle, minHeight: 30, padding: '2px 8px', whiteSpace: 'nowrap' }} onClick={() => setCalcLineIdx(i)}>💲 Calc</button>
                            {rows.length > 1 && (
                              <button title="Remove line" style={{ ...secondaryButtonStyle, minHeight: 30, padding: '2px 8px' }} onClick={() => setRows((prev) => prev.filter((_, j) => j !== i))}>✕</button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginTop: 'var(--spacing-3)', flexWrap: 'wrap' }}>
                <button style={secondaryButtonStyle} onClick={() => setRows((prev) => [...prev, { ...EMPTY_LINE }])}>+ Add another service</button>
              </div>
              <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 4 }}>Tip: pick a service on a line, then use its 💲 to work out the price.</p>
              {calcLineIdx != null && (
                <PriceCalculatorModal
                  onClose={() => setCalcLineIdx(null)}
                  onApply={applyCalcToLine}
                  initialService={rows[calcLineIdx] ? detectCalcService(rows[calcLineIdx]) : null}
                  initial={rows[calcLineIdx]?.calc ?? null}
                />
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'var(--spacing-4)', flexWrap: 'wrap', gap: 'var(--spacing-2)' }}>
              <span style={{ color: 'var(--color-text-muted)' }}>Subtotal (excl. GST): <strong>${subtotal.toFixed(2)}</strong></span>
              <div style={{ display: 'flex', gap: 'var(--spacing-3)', alignItems: 'center' }}>
                {error && <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-sm)' }}>{error}</span>}
                {!canCreate && !create.isPending && (
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                    {!contact ? 'Pick a contact' : !reference.trim() ? 'Add the property address' : 'Add at least one service with a qty'}
                  </span>
                )}
                <button style={{ ...primaryButtonStyle, opacity: canCreate ? 1 : 0.6 }} disabled={!canCreate} onClick={() => { setError(null); create.mutate(); }}>
                  {create.isPending ? 'Creating…' : 'Create quote in Xero'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const labelStyle: React.CSSProperties = { display: 'block', fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' };

/**
 * Open a quote from the portal: edit its line items (synced to Xero) and send it
 * to the customer. Send flow mirrors reality — mark SENT in Xero, paste the
 * online link (Xero's API won't hand it over), preview + edit the email/SMS,
 * then send both (PDF attached, email threaded onto the customer's recent email).
 */
export function QuotePortalModal({ quoteId, onClose, onChanged, onDuplicated }: { quoteId: string; onClose: () => void; onChanged: () => void; onDuplicated?: (newQuoteId: string) => void }) {
  const [rows, setRows] = useState<QuoteLine[]>([]);
  const [calcLineIdx, setCalcLineIdx] = useState<number | null>(null);
  const applyCalcToLine = (res: CalcResult) => {
    setRows((prev) => {
      if (calcLineIdx == null) return prev;
      const mapped = prev.map((row, j) => {
        if (j !== calcLineIdx) return row;
        let description = row.description;
        if (!description.trim() && res.descriptionIfEmpty) description = res.descriptionIfEmpty;
        if (res.scope) description = withScope(description, res.scope);
        return { ...row, unitAmount: res.unitAmount, description, ...(res.scopes ? { scopes: res.scopes } : {}), ...(res.calc ? { calc: res.calc } : {}) };
      });
      return [...mapped, ...(res.extraLines ?? [])];
    });
    setCalcLineIdx(null);
  };
  const [reference, setReference] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [mode, setMode] = useState<'edit' | 'send'>('edit');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedNote, setSavedNote] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 640);

  // Send-flow state
  const [link, setLink] = useState('');
  const [preview, setPreview] = useState<Awaited<ReturnType<typeof api.getQuoteSendPreview>> | null>(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [smsBody, setSmsBody] = useState('');
  const [toEmail, setToEmail] = useState('');
  const [toMobile, setToMobile] = useState('');
  const [sendResult, setSendResult] = useState<{ email: { attempted: boolean; sent: boolean; error?: string }; sms: { attempted: boolean; sent: boolean; error?: string } } | null>(null);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 640);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const qc = useQueryClient();
  const { data: quote, isLoading, isError, error } = useQuery({
    queryKey: ['portal-quote', quoteId],
    queryFn: () => api.getQuote(quoteId),
  });
  const { data: items = [], isLoading: itemsLoading } = useQuery({ queryKey: ['xero-items'], queryFn: () => api.getXeroItems() });
  // Saved price-calculator inputs per line, so the calculator re-populates on reopen.
  const { data: lineCalc } = useQuery({ queryKey: ['portal-quote-calc', quoteId], queryFn: () => api.getQuoteLineCalc(quoteId) });

  // Seed the editor once the quote (and its saved calc) loads.
  useEffect(() => {
    if (!quote) return;
    const calcByIdx = new Map((lineCalc?.items ?? []).map((x) => [x.lineIndex, x.calc]));
    setRows(
      (quote.lineItems ?? []).map((li: any, i: number) => ({
        itemCode: li.itemCode || '',
        description: li.description || '',
        quantity: typeof li.quantity === 'number' ? li.quantity : Number(li.quantity ?? 1),
        unitAmount: typeof li.unitAmount === 'number' ? li.unitAmount : Number(li.unitAmount ?? 0),
        discountRate: li.discountRate || 0,
        accountCode: li.accountCode || '200',
        taxType: li.taxType || '',
        ...(calcByIdx.has(i) ? { calc: calcByIdx.get(i) } : {}),
      })),
    );
    setReference(quote.reference || '');
    setStatus(quote.status || null);
    setLink(quote.onlineUrl || '');
  }, [quote, lineCalc]);

  const editable = status === 'DRAFT' || status === 'SENT';
  const subtotal = rows.reduce((n, r) => n + r.quantity * r.unitAmount * (1 - (r.discountRate || 0) / 100), 0);

  function setRow(i: number, patch: Partial<QuoteLine>) {
    setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  }
  function onPickItem(i: number, code: string) {
    const it = (items as any[]).find((x) => x.code === code);
    if (!it) { setRow(i, { itemCode: '' }); return; }
    setRow(i, { itemCode: it.code, description: it.description || it.name || '', unitAmount: typeof it.unitPrice === 'number' ? it.unitPrice : 0, accountCode: it.accountCode || '200', taxType: it.taxType || '' });
  }

  const saveMut = useMutation({
    mutationFn: async () => {
      const res = await api.updateQuote(quoteId, {
        reference: reference || null,
        lineItems: rows.filter((r) => r.description.trim()).map((r) => ({ itemCode: r.itemCode || null, description: r.description, quantity: r.quantity, unitAmount: r.unitAmount, accountCode: r.accountCode || '200', taxType: r.taxType || null, discountRate: r.discountRate || null })),
      });
      // Persist calculator scopes (only when some are present, so a plain re-save
      // doesn't wipe scopes captured earlier).
      const scopes = rows.flatMap((r) => (r.scopes ?? []).map((s) => ({ service: r.itemCode || null, scope: s.scope, areaM2: s.area })));
      if (scopes.length) {
        try { await api.saveQuoteScopes(quoteId, { quoteNumber: res.quoteNumber, scopes }); } catch { /* non-fatal */ }
      }
      // Persist calculator inputs per line (index into the same filtered lines).
      // Rows carry any previously-saved calc (merged in on load), so a plain
      // re-save preserves it; a line whose calc was cleared/removed drops out.
      const kept = rows.filter((r) => r.description.trim());
      const calcs = kept.map((r, i) => (r.calc ? { lineIndex: i, calc: r.calc } : null)).filter(Boolean) as { lineIndex: number; calc: any }[];
      try { await api.saveQuoteLineCalc(quoteId, calcs); } catch { /* non-fatal */ }
      return res;
    },
    onSuccess: (r) => { setSaveError(null); setSavedNote(true); setStatus(r.status || status); onChanged(); setTimeout(() => setSavedNote(false), 2500); },
    onError: (e: any) => setSaveError(e?.message || 'Could not save the quote.'),
  });

  const markSentMut = useMutation({
    mutationFn: () => api.markQuoteSent(quoteId),
    onSuccess: (r) => { setStatus(r.status || 'SENT'); onChanged(); },
  });

  // Follow-up: open a pre-written, editable message to the customer (2 weeks
  // after sending) over email or SMS, then send. Wording comes from an editable
  // per-channel template.
  const [clarifyOpen, setClarifyOpen] = useState(false);
  const [clarifyChannel, setClarifyChannel] = useState<'email' | 'sms'>('email');
  const [clarifyTo, setClarifyTo] = useState<string | null>(null);
  const [clarifyBody, setClarifyBody] = useState('');
  const [tplOpen, setTplOpen] = useState(false);
  const [tplSubject, setTplSubject] = useState('');
  const [tplBody, setTplBody] = useState('');
  const openClarify = async (channel: 'email' | 'sms' = 'email') => {
    setClarifyOpen(true); setClarifyChannel(channel); setTplOpen(false);
    setClarifyBody('Loading…'); setClarifyTo(null);
    try { const d = await api.getFollowupDraft(quoteId, channel); setClarifyTo(d.to); setClarifyBody(d.body); }
    catch (e: any) { setClarifyBody('[Could not load the draft: ' + (e?.message || 'error') + ']'); }
  };
  const clarifyMut = useMutation({
    mutationFn: () => api.sendFollowup(quoteId, clarifyChannel, clarifyBody),
    onSuccess: () => { setTimeout(() => setClarifyOpen(false), 1800); },
  });

  // Edit the default follow-up template for the current channel (subject is
  // email-only). Persisted so it applies to future follow-ups on that channel.
  const openTpl = async () => {
    setTplOpen(true);
    try { const t = await api.getFollowupTemplate(clarifyChannel); setTplSubject(t.subject); setTplBody(t.body); } catch { /* keep empty */ }
  };
  const saveTplMut = useMutation({
    mutationFn: () => api.saveFollowupTemplate({ channel: clarifyChannel, subject: tplSubject, body: tplBody }),
    onSuccess: () => { setTplOpen(false); openClarify(clarifyChannel); },
  });

  // Decline a quote (sets DECLINED in Xero).
  const [pendingDecline, setPendingDecline] = useState(false);
  const declineMut = useMutation({
    mutationFn: () => api.declineQuote(quoteId),
    onSuccess: (r) => { setStatus(r.status || 'DECLINED'); setPendingDecline(false); onChanged(); },
  });

  // Download the quote PDF (live from Xero — save first so it reflects changes).
  const [dlError, setDlError] = useState<string | null>(null);
  const downloadMut = useMutation({
    mutationFn: async () => {
      const blob = await api.getQuotePdfBlob(quoteId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${quote?.quoteNumber || 'quote'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    },
    onError: (e: any) => setDlError(e?.message || 'Could not download the PDF.'),
    onSuccess: () => setDlError(null),
  });

  // Undo an accidental accept: revert ACCEPTED -> SENT in Xero + remove the booking.
  const [pendingUnaccept, setPendingUnaccept] = useState(false);
  const [unacceptNote, setUnacceptNote] = useState<string | null>(null);
  const unacceptMut = useMutation({
    mutationFn: () => api.unacceptQuote(quoteId),
    onSuccess: (r) => {
      setStatus(r.status || 'SENT');
      setPendingUnaccept(false);
      const n = r.removedFromSchedule ?? 0;
      setUnacceptNote(`Reverted to Sent${n > 0 ? ` and removed ${n} booking${n === 1 ? '' : 's'} from the schedule` : ' (no calendar booking found)'}.`);
      onChanged();
    },
  });

  // Copy this quote into a new DRAFT and open it.
  const duplicateMut = useMutation({
    mutationFn: () => api.duplicateQuote(quoteId),
    onSuccess: (r) => { onChanged(); if (onDuplicated) onDuplicated(r.quoteId); },
  });

  // Referral agent (a referral partner) linked to this quote.
  const { data: partners = [] } = useQuery({ queryKey: ['referral-partners'], queryFn: () => api.getReferralPartners() });
  const referralMut = useMutation({
    mutationFn: (partnerId: number | null) => api.setQuoteReferralAgent(quoteId, partnerId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['portal-quote', quoteId] }); onChanged(); },
  });

  // Change customer: pick an existing Xero contact, or add a new one.
  const [showChangeCustomer, setShowChangeCustomer] = useState(false);
  const emptyCust = { name: '', email: '', phone: '', secondName: '', secondEmail: '', secondPhone: '' };
  const [cust, setCust] = useState(emptyCust);
  const [custSearch, setCustSearch] = useState('');
  // Search the customer master (includes manual customers not yet in Xero), so a
  // local-only customer like a manually-added contact is still findable.
  const { data: custSearchData, isFetching: custSearching } = useQuery({
    queryKey: ['customer-search', custSearch],
    queryFn: () => api.listContacts({ search: custSearch.trim(), limit: 20 }),
    enabled: showChangeCustomer && custSearch.trim().length >= 2,
  });
  const custResults = custSearchData?.contacts ?? [];
  const closeChangeCustomer = () => { setShowChangeCustomer(false); setCust(emptyCust); setCustSearch(''); };
  const reassignMut = useMutation({
    mutationFn: () => api.reassignQuoteCustomer(quoteId, cust),
    onSuccess: () => { closeChangeCustomer(); qc.invalidateQueries({ queryKey: ['portal-quote', quoteId] }); onChanged(); },
  });
  // Reassign to an existing customer. If they're already in Xero we point the
  // quote straight at that contact; if not, the backend creates the Xero contact
  // from their details first.
  const reassignExistingMut = useMutation({
    mutationFn: (r: { name: string; email?: string | null; mobile?: string | null; xeroContactId?: string | null }) =>
      api.reassignQuoteCustomer(quoteId, { name: r.name, email: r.email ?? null, phone: r.mobile ?? null, existingContactId: r.xeroContactId ?? null }),
    onSuccess: () => { closeChangeCustomer(); qc.invalidateQueries({ queryKey: ['portal-quote', quoteId] }); onChanged(); },
  });

  const previewMut = useMutation({
    mutationFn: () => api.getQuoteSendPreview(quoteId, link || null),
    onSuccess: (p) => {
      setPreview(p);
      setEmailSubject(p.emailSubject);
      setEmailBody(p.emailBody);
      setSmsBody(p.smsBody);
      setToEmail(p.email || '');
      setToMobile(p.mobile || '');
    },
  });

  const sendMut = useMutation({
    mutationFn: () =>
      api.sendQuote(quoteId, {
        toEmail: toEmail || null,
        toMobile: toMobile || null,
        emailSubject,
        emailBody,
        smsBody,
        onlineUrl: link || null,
        threadId: preview?.thread.threadId || null,
        customerId: (quote?.resolvedContact?.customerId ?? null) as number | null,
      }),
    onSuccess: (r) => { setSendResult({ email: r.email, sms: r.sms }); onChanged(); },
  });

  // Note: searchInputStyle caps width at 360px and adds a bottom margin — override
  // both so inputs fill their grid cell (the description textarea especially).
  const quoteInput: React.CSSProperties = { ...searchInputStyle, minHeight: 38, width: '100%', maxWidth: 'none', marginBottom: 0, boxSizing: 'border-box' };
  const LINE_COLS = '200px minmax(240px, 1fr) 72px 110px 84px 110px 128px';

  return (
    <div role="dialog" aria-modal="true" onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'stretch', justifyContent: 'center', zIndex: 1000, padding: 'var(--spacing-2)' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...pageCardStyle, width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%', overflowY: 'auto', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0 }}>
            {quote?.quoteNumber ? `Quote ${quote.quoteNumber}` : 'Quote'}
            {quote?.contact?.name ? ` — ${quote.contact.name}` : ''}
            {status ? <span style={{ ...badgeStyleFor(status), marginLeft: 'var(--spacing-2)' }}>{status}</span> : null}
          </h2>
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            <a href={xeroDocUrl('quote', quoteId) || '#'} target="_blank" rel="noreferrer" style={{ ...secondaryButtonStyle, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }} title="Open this quote in Xero">
              Open in Xero ↗
            </a>
            <button
              style={secondaryButtonStyle}
              disabled={duplicateMut.isPending}
              onClick={() => duplicateMut.mutate()}
              title="Create a new draft quote that copies this one (same contact and services)"
            >
              {duplicateMut.isPending ? 'Copying…' : '⧉ Copy quote'}
            </button>
            {status === 'ACCEPTED' && (
              <button
                style={{ ...secondaryButtonStyle, color: 'var(--color-error)', borderColor: 'var(--color-error)' }}
                disabled={unacceptMut.isPending}
                onClick={() => setPendingUnaccept(true)}
                title="Revert this quote from Accepted back to Sent in Xero"
              >
                {unacceptMut.isPending ? 'Un-accepting…' : 'Unaccept'}
              </button>
            )}
            <button style={secondaryButtonStyle} onClick={onClose}>Close</button>
          </div>
        </div>
        {duplicateMut.isError && <p style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-sm)' }}>{(duplicateMut.error as Error)?.message}</p>}
        {unacceptMut.isError && <p style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-sm)' }}>{(unacceptMut.error as Error)?.message}</p>}
        {unacceptNote && <p style={{ color: 'var(--color-success)', fontSize: 'var(--font-size-sm)' }}>✓ {unacceptNote}</p>}

        {isLoading && <p style={{ color: 'var(--color-text-muted)' }}>Loading quote…</p>}
        {isError && <p style={{ color: 'var(--color-error)' }}>{(error as Error)?.message || 'Could not load quote.'}</p>}

        {quote && mode === 'edit' && (
          <>
            {/* Customer + referral agent. The customer receives the quote; the
                referral agent (a referral partner) is who sent the work. */}
            <div style={{ marginTop: 'var(--spacing-3)', border: '1px solid var(--color-border)', borderRadius: 8, padding: 'var(--spacing-3)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
                <div>
                  <span style={labelStyle}>Customer</span>
                  <strong>{quote.contact?.name || '—'}</strong>
                </div>
                {editable && (
                  <button style={{ ...secondaryButtonStyle, minHeight: 30 }} onClick={() => (showChangeCustomer ? closeChangeCustomer() : setShowChangeCustomer(true))}>
                    {showChangeCustomer ? 'Cancel' : 'Change customer'}
                  </button>
                )}
              </div>

              {showChangeCustomer && (
                <div style={{ marginTop: 'var(--spacing-2)', display: 'grid', gap: 6 }}>
                  {/* Pick an existing customer */}
                  <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--color-text-muted)' }}>Use an existing customer</div>
                  <input style={{ ...quoteInput }} placeholder="Search by name, email, mobile or quote #…" value={custSearch} onChange={(e) => setCustSearch(e.target.value)} />
                  {custSearch.trim().length >= 2 && (
                    <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', maxHeight: 220, overflowY: 'auto', background: 'var(--color-surface)' }}>
                      {custSearching && <div style={{ padding: 8, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>Searching…</div>}
                      {!custSearching && custResults.length === 0 && <div style={{ padding: 8, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>No matches. Add a new customer below.</div>}
                      {(custResults as any[]).map((r) => (
                        <button key={r.id} disabled={reassignExistingMut.isPending}
                          onClick={() => reassignExistingMut.mutate({ name: r.fullName, email: r.email, mobile: r.mobile, xeroContactId: r.xeroContactId })}
                          style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', borderBottom: '1px solid var(--color-border)', background: 'none', padding: '8px 10px', cursor: 'pointer' }}>
                          <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>{r.fullName}</div>
                          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                            {[r.email, r.mobile].filter(Boolean).join(' · ') || 'no contact details'}
                            {!r.inXero ? ' · not yet in Xero (will be created)' : ''}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {reassignExistingMut.isError && <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-sm)' }}>{(reassignExistingMut.error as Error)?.message}</span>}

                  {/* Or create a new customer */}
                  <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--color-text-muted)', marginTop: 'var(--spacing-2)', paddingTop: 'var(--spacing-2)', borderTop: '1px dashed var(--color-border)' }}>Or add a new customer</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                    <input style={{ ...quoteInput }} placeholder="Customer name *" value={cust.name} onChange={(e) => setCust((c) => ({ ...c, name: e.target.value }))} />
                    <input style={{ ...quoteInput }} placeholder="Email" value={cust.email} onChange={(e) => setCust((c) => ({ ...c, email: e.target.value }))} />
                    <input style={{ ...quoteInput }} placeholder="Mobile" value={cust.phone} onChange={(e) => setCust((c) => ({ ...c, phone: e.target.value }))} />
                  </div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Second contact (optional)</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                    <input style={{ ...quoteInput }} placeholder="Name" value={cust.secondName} onChange={(e) => setCust((c) => ({ ...c, secondName: e.target.value }))} />
                    <input style={{ ...quoteInput }} placeholder="Email" value={cust.secondEmail} onChange={(e) => setCust((c) => ({ ...c, secondEmail: e.target.value }))} />
                    <input style={{ ...quoteInput }} placeholder="Mobile" value={cust.secondPhone} onChange={(e) => setCust((c) => ({ ...c, secondPhone: e.target.value }))} />
                  </div>
                  <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center' }}>
                    <button style={{ ...primaryButtonStyle, opacity: cust.name.trim() && !reassignMut.isPending ? 1 : 0.5 }} disabled={!cust.name.trim() || reassignMut.isPending} onClick={() => reassignMut.mutate()}>
                      {reassignMut.isPending ? 'Reassigning…' : 'Reassign customer in Xero'}
                    </button>
                    {reassignMut.isError && <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-sm)' }}>{(reassignMut.error as Error)?.message}</span>}
                  </div>
                </div>
              )}

              <div style={{ marginTop: 'var(--spacing-3)' }}>
                <label style={labelStyle}>Referral agent <span style={{ fontWeight: 400, textTransform: 'none' }}>(who sent this job)</span></label>
                <select
                  style={{ ...quoteInput }}
                  value={quote.referralAgent?.partnerId ?? ''}
                  onChange={(e) => referralMut.mutate(e.target.value ? Number(e.target.value) : null)}
                  disabled={referralMut.isPending}
                >
                  <option value="">— None —</option>
                  {(partners as any[]).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 4 }}>
                  Manage partners on the Referrals page. {referralMut.isError && <span style={{ color: 'var(--color-error)' }}>Couldn’t save.</span>}
                </div>
              </div>
            </div>

            {/* The customer's original website submission this quote was created
                from (linked at creation). Collapsed by default: the office
                wanted the quote first and the request a click away. */}
            {quote.websiteRequest && (
              <details style={{ marginTop: 'var(--spacing-3)', border: '1px solid var(--color-border)', borderRadius: 8, padding: 'var(--spacing-2) var(--spacing-3)' }}>
                <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>
                  🌐 Original website request
                  <span style={{ fontWeight: 400, color: 'var(--color-text-muted)' }}>
                    {quote.websiteRequest.receivedAt ? ` · received ${bneDateTime(quote.websiteRequest.receivedAt)}` : ''}
                    {quote.websiteRequest.source ? ` · came from ${quote.websiteRequest.source}` : ''}
                  </span>
                </summary>
                <div style={{ whiteSpace: 'pre-wrap', fontSize: 'var(--font-size-sm)', marginTop: 'var(--spacing-2)', color: 'var(--color-text)' }}>
                  {quote.websiteRequest.summary ?? '—'}
                </div>
                {(quote.websiteRequest.images?.length ?? 0) > 0 && (
                  <div style={{ marginTop: 'var(--spacing-2)' }}>
                    <ObservationImages observationId={quote.websiteRequest.id} tokens={quote.websiteRequest.images} />
                  </div>
                )}
              </details>
            )}

            <div style={{ marginTop: 'var(--spacing-3)' }}>
              <label style={labelStyle}>Property address / reference</label>
              <input style={{ ...quoteInput }} value={reference} onChange={(e) => setReference(e.target.value)} disabled={!editable} />
            </div>

            <div style={{ marginTop: 'var(--spacing-4)' }}>
              <h3 style={{ margin: 0, fontSize: 'var(--font-size-md)', marginBottom: 'var(--spacing-2)' }}>Services</h3>
              {!editable && (
                <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
                  This quote is {status} in Xero and can’t be edited here.
                  {status === 'ACCEPTED' && ' Use “Unaccept” above to revert it to Sent if it was accepted by mistake.'}
                </p>
              )}
              {isMobile ? (
                <div style={{ display: 'grid', gap: 'var(--spacing-3)' }}>
                  {rows.map((r, i) => {
                    const amount = r.quantity * r.unitAmount * (1 - (r.discountRate || 0) / 100);
                    return (
                      <div key={i} style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: 'var(--spacing-3)', display: 'grid', gap: 8 }}>
                        <div><label style={labelStyle}>Service</label>
                          <select style={{ ...quoteInput, height: 40 }} value={r.itemCode} onChange={(e) => onPickItem(i, e.target.value)} disabled={!editable || itemsLoading}>
                            <option value="">{itemsLoading ? 'Loading…' : 'Select…'}</option>
                            {(items as any[]).map((it) => (<option key={it.code} value={it.code}>{it.code} — {it.name}</option>))}
                          </select></div>
                        <div><label style={labelStyle}>Description</label><textarea style={{ ...quoteInput, minHeight: 100, resize: 'vertical', lineHeight: 1.4 }} value={r.description} onChange={(e) => setRow(i, { description: e.target.value })} disabled={!editable} /></div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                          <div><label style={labelStyle}>Qty</label><input type="number" min={0} style={{ ...quoteInput, height: 40 }} value={r.quantity} onChange={(e) => setRow(i, { quantity: Number(e.target.value) })} disabled={!editable} /></div>
                          <div><label style={labelStyle}>Price</label><input type="number" step="0.01" min={0} style={{ ...quoteInput, height: 40 }} value={r.unitAmount} onChange={(e) => setRow(i, { unitAmount: Number(e.target.value) })} disabled={!editable} /></div>
                          <div><label style={labelStyle}>Disc %</label><input type="number" min={0} max={100} style={{ ...quoteInput, height: 40 }} value={r.discountRate} onChange={(e) => setRow(i, { discountRate: Number(e.target.value) })} disabled={!editable} /></div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontWeight: 600 }}>Amount: ${amount.toFixed(2)}</span>
                          {editable && (
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button title="Work out the price for this service" style={{ ...secondaryButtonStyle, minHeight: 32 }} onClick={() => setCalcLineIdx(i)}>💲 Calculate</button>
                              {rows.length > 1 && (<button style={{ ...secondaryButtonStyle, minHeight: 32, color: 'var(--color-error)' }} onClick={() => setRows((prev) => prev.filter((_, j) => j !== i))}>Remove</button>)}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <div style={{ minWidth: 720 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: LINE_COLS, gap: 'var(--spacing-2)', padding: '0 4px var(--spacing-1)', borderBottom: '1px solid var(--color-border)' }}>
                      {['Service', 'Description', 'Qty', 'Price', 'Disc %', 'Amount', ''].map((h, i) => (<div key={i} style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', textAlign: i >= 2 && i <= 5 ? 'right' : 'left' }}>{h}</div>))}
                    </div>
                    {rows.map((r, i) => {
                      const amount = r.quantity * r.unitAmount * (1 - (r.discountRate || 0) / 100);
                      return (
                        <div key={i} style={{ display: 'grid', gridTemplateColumns: LINE_COLS, gap: 'var(--spacing-2)', alignItems: 'start', padding: 'var(--spacing-2) 4px', borderBottom: '1px solid var(--color-border)' }}>
                          <select style={{ ...quoteInput, height: 38 }} value={r.itemCode} onChange={(e) => onPickItem(i, e.target.value)} disabled={!editable || itemsLoading}>
                            <option value="">{itemsLoading ? 'Loading…' : 'Select…'}</option>
                            {(items as any[]).map((it) => (<option key={it.code} value={it.code}>{it.code} — {it.name}</option>))}
                          </select>
                          <textarea style={{ ...quoteInput, minHeight: 90, resize: 'vertical', lineHeight: 1.4 }} value={r.description} onChange={(e) => setRow(i, { description: e.target.value })} disabled={!editable} />
                          <input type="number" min={0} style={{ ...quoteInput, height: 38, textAlign: 'right' }} value={r.quantity} onChange={(e) => setRow(i, { quantity: Number(e.target.value) })} disabled={!editable} />
                          <input type="number" step="0.01" min={0} style={{ ...quoteInput, height: 38, textAlign: 'right' }} value={r.unitAmount} onChange={(e) => setRow(i, { unitAmount: Number(e.target.value) })} disabled={!editable} />
                          <input type="number" min={0} max={100} style={{ ...quoteInput, height: 38, textAlign: 'right' }} value={r.discountRate} onChange={(e) => setRow(i, { discountRate: Number(e.target.value) })} disabled={!editable} />
                          <div style={{ textAlign: 'right', paddingTop: 9, fontWeight: 600, whiteSpace: 'nowrap' }}>${amount.toFixed(2)}</div>
                          <div style={{ paddingTop: 4, display: 'flex', gap: 4 }}>
                            {editable && <button title="Work out the price for this service" style={{ ...secondaryButtonStyle, minHeight: 30, padding: '2px 8px', whiteSpace: 'nowrap' }} onClick={() => setCalcLineIdx(i)}>💲 Calc</button>}
                            {editable && rows.length > 1 && (<button title="Remove line" style={{ ...secondaryButtonStyle, minHeight: 30, padding: '2px 8px' }} onClick={() => setRows((prev) => prev.filter((_, j) => j !== i))}>✕</button>)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {editable && (
                <>
                  <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginTop: 'var(--spacing-3)', flexWrap: 'wrap' }}>
                    <button style={secondaryButtonStyle} onClick={() => setRows((prev) => [...prev, { ...EMPTY_LINE }])}>+ Add another service</button>
                  </div>
                  <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 4 }}>Tip: pick a service on a line, then use its 💲 to work out the price.</p>
                </>
              )}
              {calcLineIdx != null && (
                <PriceCalculatorModal
                  onClose={() => setCalcLineIdx(null)}
                  onApply={applyCalcToLine}
                  initialService={rows[calcLineIdx] ? detectCalcService(rows[calcLineIdx]) : null}
                  initial={rows[calcLineIdx]?.calc ?? null}
                />
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'var(--spacing-4)', flexWrap: 'wrap', gap: 'var(--spacing-2)' }}>
              <span style={{ color: 'var(--color-text-muted)' }}>Subtotal (excl. GST): <strong>${subtotal.toFixed(2)}</strong></span>
              <div style={{ display: 'flex', gap: 'var(--spacing-3)', alignItems: 'center', flexWrap: 'wrap' }}>
                {saveError && <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-sm)' }}>{saveError}</span>}
                {savedNote && <span style={{ color: 'var(--color-success)', fontSize: 'var(--font-size-sm)' }}>✓ Saved to Xero</span>}
                {dlError && <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-sm)' }}>{dlError}</span>}
                <button style={{ ...secondaryButtonStyle }} disabled={downloadMut.isPending} onClick={() => downloadMut.mutate()} title="Download this quote as a PDF (from Xero). Save first so it reflects your changes.">
                  {downloadMut.isPending ? 'Preparing…' : '⬇ Download PDF'}
                </button>
                {editable && (
                  <button style={{ ...secondaryButtonStyle }} disabled={saveMut.isPending} onClick={() => { setSaveError(null); saveMut.mutate(); }}>
                    {saveMut.isPending ? 'Saving…' : 'Save changes'}
                  </button>
                )}
                {status === 'SENT' && (
                  <button style={{ ...secondaryButtonStyle }} onClick={() => openClarify('email')} title="Send this customer a follow-up on the quote by email or SMS (recommended ~2 weeks after sending)">✉ Send follow-up</button>
                )}
                {(status === 'DRAFT' || status === 'SENT') && (
                  <button style={{ ...secondaryButtonStyle, borderColor: 'var(--color-error)', color: 'var(--color-error)' }} disabled={declineMut.isPending} onClick={() => setPendingDecline(true)} title="Mark this quote as Declined in Xero">
                    {declineMut.isPending ? 'Declining…' : '✕ Decline'}
                  </button>
                )}
                <button style={{ ...primaryButtonStyle }} onClick={() => setMode('send')}>Send quote →</button>
              </div>
              {declineMut.isError && <p style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-sm)' }}>{(declineMut.error as Error)?.message}</p>}
              {clarifyOpen && (
                <div style={{ marginTop: 'var(--spacing-3)', padding: 'var(--spacing-3)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', background: 'var(--color-surface-elevated)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, gap: 8, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <strong style={{ fontSize: 'var(--font-size-sm)' }}>Follow-up{clarifyTo ? ` → ${clarifyTo}` : ''}</strong>
                      <div style={{ display: 'inline-flex', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', overflow: 'hidden' }}>
                        {(['email', 'sms'] as const).map((ch) => (
                          <button key={ch} onClick={() => openClarify(ch)}
                            style={{ border: 'none', padding: '4px 12px', fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: 'pointer',
                              background: clarifyChannel === ch ? 'var(--color-teal)' : 'transparent',
                              color: clarifyChannel === ch ? '#fff' : 'var(--color-text-muted)' }}>
                            {ch === 'email' ? 'Email' : 'SMS'}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center' }}>
                      <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--font-size-xs)', color: 'var(--color-navy)', textDecoration: 'underline' }} onClick={openTpl}>Edit default wording</button>
                      <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: 'var(--color-text-muted)' }} onClick={() => setClarifyOpen(false)}>✕</button>
                    </div>
                  </div>
                  {!clarifyTo && clarifyBody && !clarifyBody.startsWith('Loading') && (
                    <p style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-xs)', marginTop: 0 }}>No {clarifyChannel === 'sms' ? 'mobile number' : 'email address'} on file for this contact. Add one on the contact first.</p>
                  )}
                  <textarea value={clarifyBody} onChange={(e) => setClarifyBody(e.target.value)} rows={clarifyChannel === 'sms' ? 5 : 9}
                    style={{ width: '100%', fontFamily: 'inherit', fontSize: 'var(--font-size-sm)', padding: 'var(--spacing-2)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', resize: 'vertical', whiteSpace: 'pre-wrap', boxSizing: 'border-box' }} />
                  <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center', marginTop: 'var(--spacing-2)' }}>
                    <button style={{ ...primaryButtonStyle }} disabled={!clarifyTo || clarifyMut.isPending} onClick={() => clarifyMut.mutate()}>{clarifyMut.isPending ? 'Sending…' : (clarifyChannel === 'sms' ? 'Send SMS' : 'Send email')}</button>
                    <button style={{ ...secondaryButtonStyle }} onClick={() => setClarifyOpen(false)}>Cancel</button>
                    {clarifyMut.isSuccess && (clarifyMut.data as any)?.sent && <span style={{ color: 'var(--color-success)', fontSize: 'var(--font-size-sm)' }}>✓ Sent to {(clarifyMut.data as any).to}</span>}
                    {clarifyMut.isError && <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-sm)' }}>{(clarifyMut.error as Error).message}</span>}
                  </div>
                  {tplOpen && (
                    <div style={{ marginTop: 'var(--spacing-3)', paddingTop: 'var(--spacing-3)', borderTop: '1px dashed var(--color-border)' }}>
                      <strong style={{ fontSize: 'var(--font-size-sm)' }}>Default follow-up template ({clarifyChannel === 'sms' ? 'SMS' : 'Email'})</strong>
                      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', margin: '2px 0 8px' }}>
                        Use <code>{'{name}'}</code> for the contact's first name and <code>{'{quote_number}'}</code> for the quote number. The online-link line is added automatically. Saving updates the default for future {clarifyChannel === 'sms' ? 'SMS' : 'email'} follow-ups.
                      </p>
                      {clarifyChannel === 'email' && (
                        <input value={tplSubject} onChange={(e) => setTplSubject(e.target.value)} placeholder="Subject"
                          style={{ width: '100%', fontSize: 'var(--font-size-sm)', padding: 'var(--spacing-2)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', boxSizing: 'border-box', marginBottom: 'var(--spacing-2)' }} />
                      )}
                      <textarea value={tplBody} onChange={(e) => setTplBody(e.target.value)} rows={clarifyChannel === 'sms' ? 5 : 9}
                        style={{ width: '100%', fontFamily: 'inherit', fontSize: 'var(--font-size-sm)', padding: 'var(--spacing-2)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', resize: 'vertical', whiteSpace: 'pre-wrap', boxSizing: 'border-box' }} />
                      <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center', marginTop: 'var(--spacing-2)' }}>
                        <button style={{ ...primaryButtonStyle }} disabled={!tplBody.trim() || saveTplMut.isPending} onClick={() => saveTplMut.mutate()}>{saveTplMut.isPending ? 'Saving…' : 'Save template'}</button>
                        <button style={{ ...secondaryButtonStyle }} onClick={() => setTplOpen(false)}>Cancel</button>
                        {saveTplMut.isError && <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-sm)' }}>{(saveTplMut.error as Error).message}</span>}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {quote && mode === 'send' && (
          <div style={{ marginTop: 'var(--spacing-3)', display: 'grid', gap: 'var(--spacing-4)' }}>
            <button style={{ ...secondaryButtonStyle, alignSelf: 'flex-start' }} onClick={() => setMode('edit')}>← Back to quote</button>

            {/* Step 1 — mark sent */}
            <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: 'var(--spacing-3)' }}>
              <h3 style={{ margin: '0 0 6px', fontSize: 'var(--font-size-md)' }}>1. Mark as sent in Xero</h3>
              {status === 'SENT' || status === 'ACCEPTED' || status === 'INVOICED' ? (
                <p style={{ margin: 0, color: 'var(--color-success)' }}>✓ This quote is {status} in Xero.</p>
              ) : (
                <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center', flexWrap: 'wrap' }}>
                  <button style={primaryButtonStyle} disabled={markSentMut.isPending} onClick={() => markSentMut.mutate()}>
                    {markSentMut.isPending ? 'Marking…' : 'Mark as sent in Xero'}
                  </button>
                  {markSentMut.isError && <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-sm)' }}>{(markSentMut.error as Error)?.message}</span>}
                </div>
              )}
            </div>

            {/* Step 2 — paste the online link */}
            <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: 'var(--spacing-3)' }}>
              <h3 style={{ margin: '0 0 6px', fontSize: 'var(--font-size-md)' }}>2. Paste the Xero online link <span style={{ fontWeight: 400, color: 'var(--color-text-muted)' }}>(optional)</span></h3>
              <p style={{ margin: '0 0 8px', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
                <a href={xeroDocUrl('quote', quoteId) || '#'} target="_blank" rel="noreferrer" style={{ color: 'var(--color-accent)', fontWeight: 600 }}>Open this quote in Xero ↗</a>, use <strong>Copy link</strong>, then paste it here so the customer can accept online. Leave blank to send the PDF only.
              </p>
              <input style={{ ...quoteInput }} placeholder="https://in.xero.com/…" value={link} onChange={(e) => setLink(e.target.value)} />
            </div>

            {/* Step 3 — preview + send */}
            <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: 'var(--spacing-3)' }}>
              <h3 style={{ margin: '0 0 6px', fontSize: 'var(--font-size-md)' }}>3. Preview &amp; send</h3>
              {!preview ? (
                <button style={primaryButtonStyle} disabled={previewMut.isPending} onClick={() => previewMut.mutate()}>
                  {previewMut.isPending ? 'Building preview…' : 'Build preview'}
                </button>
              ) : (
                <div style={{ display: 'grid', gap: 'var(--spacing-3)' }}>
                  {preview.thread.found ? (
                    <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-success)' }}>
                      ↩ Replying to the customer’s email “{preview.thread.subject}” ({preview.thread.date}).
                    </p>
                  ) : (
                    <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>No recent email from the customer in the last 2 days — this goes as a new email.</p>
                  )}

                  {/* Email */}
                  <div>
                    <label style={labelStyle}>Email to</label>
                    <input style={{ ...quoteInput }} value={toEmail} onChange={(e) => setToEmail(e.target.value)} placeholder="No email on file" />
                    <label style={{ ...labelStyle, marginTop: 8 }}>Subject</label>
                    <input style={{ ...quoteInput }} value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} />
                    <label style={{ ...labelStyle, marginTop: 8 }}>Email body <span style={{ fontWeight: 400, textTransform: 'none' }}>(quote PDF is attached automatically)</span></label>
                    <textarea style={{ ...quoteInput, minHeight: 220, resize: 'vertical', lineHeight: 1.5 }} value={emailBody} onChange={(e) => setEmailBody(e.target.value)} />
                  </div>

                  {/* SMS */}
                  <div>
                    <label style={labelStyle}>SMS to</label>
                    <input style={{ ...quoteInput }} value={toMobile} onChange={(e) => setToMobile(e.target.value)} placeholder="No mobile on file" />
                    <label style={{ ...labelStyle, marginTop: 8 }}>SMS text <span style={{ fontWeight: 400, textTransform: 'none' }}>(quote PDF is attached automatically)</span></label>
                    <textarea style={{ ...quoteInput, minHeight: 90, resize: 'vertical', lineHeight: 1.4 }} value={smsBody} onChange={(e) => setSmsBody(e.target.value)} />
                  </div>

                  {sendResult ? (
                    <div style={{ background: 'var(--color-surface-elevated)', border: '1px solid var(--color-border)', borderRadius: 8, padding: 'var(--spacing-3)' }}>
                      <p style={{ margin: '0 0 4px', fontWeight: 700, color: 'var(--color-success)' }}>Sent.</p>
                      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 'var(--font-size-sm)' }}>
                        <li>Email: {sendResult.email.attempted ? (sendResult.email.sent ? '✓ sent' : `✗ ${sendResult.email.error || 'failed'}`) : 'skipped (no email)'}</li>
                        <li>SMS: {sendResult.sms.attempted ? (sendResult.sms.sent ? '✓ sent' : `✗ ${sendResult.sms.error || 'failed'}`) : 'skipped (no mobile)'}</li>
                      </ul>
                      <button style={{ ...primaryButtonStyle, marginTop: 'var(--spacing-2)' }} onClick={onClose}>Done</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 'var(--spacing-3)', alignItems: 'center', flexWrap: 'wrap' }}>
                      {sendMut.isError && <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-sm)' }}>{(sendMut.error as Error)?.message}</span>}
                      {!toEmail && !toMobile && <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-sm)' }}>No email or mobile to send to.</span>}
                      <button style={{ ...secondaryButtonStyle }} disabled={previewMut.isPending} onClick={() => previewMut.mutate()} title="Rebuild the preview with the current link">↻ Refresh preview</button>
                      <button style={{ ...primaryButtonStyle, opacity: (toEmail || toMobile) && !sendMut.isPending ? 1 : 0.6 }} disabled={(!toEmail && !toMobile) || sendMut.isPending} onClick={() => sendMut.mutate()}>
                        {sendMut.isPending ? 'Sending…' : 'Send to customer'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <ConfirmDialog
          isOpen={pendingUnaccept}
          title={`Unaccept ${quote?.quoteNumber ? `quote ${quote.quoteNumber}` : 'this quote'}?`}
          message="This reverts the quote from Accepted back to Sent in Xero and removes its booked job(s) from the schedule."
          tone="warning"
          confirmLabel={unacceptMut.isPending ? 'Un-accepting…' : 'Unaccept'}
          onConfirm={() => unacceptMut.mutate()}
          onCancel={() => setPendingUnaccept(false)}
        />

        <ConfirmDialog
          isOpen={pendingDecline}
          title={`Decline ${quote?.quoteNumber ? `quote ${quote.quoteNumber}` : 'this quote'}?`}
          message="This marks the quote as Declined in Xero. You can still duplicate it later if the customer comes back."
          tone="danger"
          confirmLabel={declineMut.isPending ? 'Declining…' : 'Decline quote'}
          onConfirm={() => declineMut.mutate()}
          onCancel={() => setPendingDecline(false)}
        />
      </div>
    </div>
  );
}

/**
 * Open an invoice from the portal and send it to the customer — the invoice
 * sibling of QuotePortalModal. Mark it sent in Xero (SentToContact), the online
 * "pay" link auto-fills from Xero, then preview + edit the email/SMS and send both
 * (PDF attached, email threaded onto the customer's recent email).
 */
function InvoicePortalModal({ invoiceId, onClose, onChanged }: { invoiceId: string; onClose: () => void; onChanged: () => void }) {
  const [link, setLink] = useState('');
  const [linkSeeded, setLinkSeeded] = useState(false);
  const [preview, setPreview] = useState<Awaited<ReturnType<typeof api.getInvoiceSendPreview>> | null>(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [smsBody, setSmsBody] = useState('');
  const [toEmail, setToEmail] = useState('');
  const [toMobile, setToMobile] = useState('');
  const [sentToContact, setSentToContact] = useState(false);
  const [sendResult, setSendResult] = useState<{ email: { attempted: boolean; sent: boolean; error?: string }; sms: { attempted: boolean; sent: boolean; error?: string } } | null>(null);

  const { data: invoice, isLoading, isError, error } = useQuery({
    queryKey: ['portal-invoice', invoiceId],
    queryFn: () => api.getInvoice(invoiceId),
  });

  // Seed the online link + sent state once the invoice loads.
  useEffect(() => {
    if (!invoice) return;
    setSentToContact(invoice.sentToContact === true);
    if (!linkSeeded) { setLink(invoice.onlineUrl || ''); setLinkSeeded(true); }
  }, [invoice, linkSeeded]);

  const status: string | null = invoice?.status ?? null;

  const markSentMut = useMutation({
    mutationFn: () => api.markInvoiceSent(invoiceId),
    onSuccess: (r) => { setSentToContact(r.sentToContact); onChanged(); },
  });

  // First build: omit the link so the backend auto-fetches Xero's online-invoice
  // URL. Refreshes pass the (possibly-edited) link explicitly.
  const previewMut = useMutation({
    mutationFn: (useLink: boolean) => api.getInvoiceSendPreview(invoiceId, useLink ? (link || '') : undefined),
    onSuccess: (p) => {
      setPreview(p);
      setEmailSubject(p.emailSubject);
      setEmailBody(p.emailBody);
      setSmsBody(p.smsBody);
      setToEmail(p.email || '');
      setToMobile(p.mobile || '');
      if (!linkSeeded || !link) { setLink(p.onlineUrl || ''); setLinkSeeded(true); }
    },
  });

  const sendMut = useMutation({
    mutationFn: () =>
      api.sendInvoice(invoiceId, {
        toEmail: toEmail || null,
        toMobile: toMobile || null,
        emailSubject,
        emailBody,
        smsBody,
        onlineUrl: link || null,
        threadId: preview?.thread.threadId || null,
        customerId: (invoice?.resolvedContact?.customerId ?? null) as number | null,
      }),
    onSuccess: (r) => { setSendResult({ email: r.email, sms: r.sms }); onChanged(); },
  });

  const inp: React.CSSProperties = { ...searchInputStyle, minHeight: 38, width: '100%', maxWidth: 'none', marginBottom: 0, boxSizing: 'border-box' };

  return (
    <div role="dialog" aria-modal="true" onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'stretch', justifyContent: 'center', zIndex: 1000, padding: 'var(--spacing-2)' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...pageCardStyle, width: '100%', maxWidth: 760, height: '100%', maxHeight: '100%', overflowY: 'auto', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0 }}>
            {invoice?.invoiceNumber ? `Invoice ${invoice.invoiceNumber}` : 'Invoice'}
            {invoice?.contact?.name ? ` — ${invoice.contact.name}` : ''}
            {status ? <span style={{ ...badgeStyleFor(status), marginLeft: 'var(--spacing-2)' }}>{status}</span> : null}
          </h2>
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            <a href={xeroDocUrl('invoice', invoiceId) || '#'} target="_blank" rel="noreferrer" style={{ ...secondaryButtonStyle, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }} title="Open this invoice in Xero">
              Open in Xero ↗
            </a>
            <button style={secondaryButtonStyle} onClick={onClose}>Close</button>
          </div>
        </div>

        {isLoading && <p style={{ color: 'var(--color-text-muted)' }}>Loading invoice…</p>}
        {isError && <p style={{ color: 'var(--color-error)' }}>{(error as Error)?.message || 'Could not load invoice.'}</p>}

        {invoice && (
          <div style={{ marginTop: 'var(--spacing-3)', display: 'grid', gap: 'var(--spacing-4)' }}>
            {/* Summary */}
            <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: 'var(--spacing-3)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 'var(--spacing-3)' }}>
              <div><div style={labelStyle}>Customer</div><strong>{invoice.contact?.name || '—'}</strong></div>
              <div><div style={labelStyle}>Total</div><strong>{money(invoice.total)}</strong></div>
              <div><div style={labelStyle}>Amount due</div><strong>{money(invoice.amountDue)}</strong></div>
              <div><div style={labelStyle}>Due date</div><strong>{invoice.dueDate || '—'}</strong></div>
            </div>

            {/* Step 1 — mark sent */}
            <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: 'var(--spacing-3)' }}>
              <h3 style={{ margin: '0 0 6px', fontSize: 'var(--font-size-md)' }}>1. Mark as sent in Xero</h3>
              {sentToContact ? (
                <p style={{ margin: 0, color: 'var(--color-success)' }}>✓ This invoice is marked as sent in Xero.</p>
              ) : (
                <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center', flexWrap: 'wrap' }}>
                  <button style={primaryButtonStyle} disabled={markSentMut.isPending} onClick={() => markSentMut.mutate()}>
                    {markSentMut.isPending ? 'Marking…' : 'Mark as sent in Xero'}
                  </button>
                  {status && status !== 'AUTHORISED' && status !== 'PAID' && (
                    <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>This invoice is {status}; authorise it in Xero first if this fails.</span>
                  )}
                  {markSentMut.isError && <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-sm)' }}>{(markSentMut.error as Error)?.message}</span>}
                </div>
              )}
            </div>

            {/* Step 2 — online pay link */}
            <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: 'var(--spacing-3)' }}>
              <h3 style={{ margin: '0 0 6px', fontSize: 'var(--font-size-md)' }}>2. Online “pay” link <span style={{ fontWeight: 400, color: 'var(--color-text-muted)' }}>(optional)</span></h3>
              <p style={{ margin: '0 0 8px', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
                Auto-filled from Xero when available. Edit it or clear it to send the PDF only.
              </p>
              <input style={inp} placeholder="https://in.xero.com/…" value={link} onChange={(e) => setLink(e.target.value)} />
            </div>

            {/* Step 3 — preview + send */}
            <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: 'var(--spacing-3)' }}>
              <h3 style={{ margin: '0 0 6px', fontSize: 'var(--font-size-md)' }}>3. Preview &amp; send</h3>
              {!preview ? (
                <button style={primaryButtonStyle} disabled={previewMut.isPending} onClick={() => previewMut.mutate(false)}>
                  {previewMut.isPending ? 'Building preview…' : 'Build preview'}
                </button>
              ) : (
                <div style={{ display: 'grid', gap: 'var(--spacing-3)' }}>
                  {preview.thread.found ? (
                    <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-success)' }}>
                      ↩ Replying to the customer’s email “{preview.thread.subject}” ({preview.thread.date}).
                    </p>
                  ) : (
                    <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>No recent email from the customer in the last 2 days — this goes as a new email.</p>
                  )}

                  {/* Email */}
                  <div>
                    <label style={labelStyle}>Email to</label>
                    <input style={inp} value={toEmail} onChange={(e) => setToEmail(e.target.value)} placeholder="No email on file" />
                    <label style={{ ...labelStyle, marginTop: 8 }}>Subject</label>
                    <input style={inp} value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} />
                    <label style={{ ...labelStyle, marginTop: 8 }}>Email body <span style={{ fontWeight: 400, textTransform: 'none' }}>(invoice PDF is attached automatically)</span></label>
                    <textarea style={{ ...inp, minHeight: 220, resize: 'vertical', lineHeight: 1.5 }} value={emailBody} onChange={(e) => setEmailBody(e.target.value)} />
                  </div>

                  {/* SMS */}
                  <div>
                    <label style={labelStyle}>SMS to</label>
                    <input style={inp} value={toMobile} onChange={(e) => setToMobile(e.target.value)} placeholder="No mobile on file" />
                    <label style={{ ...labelStyle, marginTop: 8 }}>SMS text <span style={{ fontWeight: 400, textTransform: 'none' }}>(invoice PDF is attached automatically)</span></label>
                    <textarea style={{ ...inp, minHeight: 90, resize: 'vertical', lineHeight: 1.4 }} value={smsBody} onChange={(e) => setSmsBody(e.target.value)} />
                  </div>

                  {sendResult ? (
                    <div style={{ background: 'var(--color-surface-elevated)', border: '1px solid var(--color-border)', borderRadius: 8, padding: 'var(--spacing-3)' }}>
                      <p style={{ margin: '0 0 4px', fontWeight: 700, color: 'var(--color-success)' }}>Sent.</p>
                      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 'var(--font-size-sm)' }}>
                        <li>Email: {sendResult.email.attempted ? (sendResult.email.sent ? '✓ sent' : `✗ ${sendResult.email.error || 'failed'}`) : 'skipped (no email)'}</li>
                        <li>SMS: {sendResult.sms.attempted ? (sendResult.sms.sent ? '✓ sent' : `✗ ${sendResult.sms.error || 'failed'}`) : 'skipped (no mobile)'}</li>
                      </ul>
                      <button style={{ ...primaryButtonStyle, marginTop: 'var(--spacing-2)' }} onClick={onClose}>Done</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 'var(--spacing-3)', alignItems: 'center', flexWrap: 'wrap' }}>
                      {sendMut.isError && <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-sm)' }}>{(sendMut.error as Error)?.message}</span>}
                      {!toEmail && !toMobile && <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-sm)' }}>No email or mobile to send to.</span>}
                      <button style={{ ...secondaryButtonStyle }} disabled={previewMut.isPending} onClick={() => previewMut.mutate(true)} title="Rebuild the preview with the current link">↻ Refresh preview</button>
                      <button style={{ ...primaryButtonStyle, opacity: (toEmail || toMobile) && !sendMut.isPending ? 1 : 0.6 }} disabled={(!toEmail && !toMobile) || sendMut.isPending} onClick={() => sendMut.mutate()}>
                        {sendMut.isPending ? 'Sending…' : 'Send to customer'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Detail view for one website quote-request observation. This is NOT a Xero
 * quote — it stays an assistant_observation row until the admin reads it and
 * manually creates the real quote via the existing + New quote button; this
 * modal only offers Close and Mark handled (archive), nothing else.
 */
function QuoteRequestModal({
  observation,
  onClose,
  onArchive,
  onCreateQuote,
  onOpenQuote,
  archiving,
}: {
  observation: AssistantObservation | null;
  onClose: () => void;
  onArchive: () => void;
  onCreateQuote: () => void;
  onOpenQuote: (quoteId: string) => void;
  archiving: boolean;
}) {
  if (!observation) return null;
  const quoted = !!observation.quoteId;
  return (
    <div role="dialog" aria-modal="true" onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'stretch', justifyContent: 'center', zIndex: 1000, padding: 'var(--spacing-2)' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...pageCardStyle, width: '100%', maxWidth: 640, height: '100%', maxHeight: '100%', overflowY: 'auto', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0 }}>{observation.subject ?? 'New Website Quote Request'}</h2>
          <button style={secondaryButtonStyle} onClick={onClose}>Close</button>
        </div>
        <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', margin: 'var(--spacing-1) 0 var(--spacing-3)' }}>
          {observation.fromName ?? 'Unknown sender'}
          {observation.fromEmail ? ` · ${observation.fromEmail}` : ''}
          {observation.receivedAt ? ` · Received ${bneDateTime(observation.receivedAt)}` : ''}
        </div>
        <div style={{ whiteSpace: 'pre-wrap', fontSize: 'var(--font-size-sm)', border: '1px solid var(--color-border)', borderRadius: 8, padding: 'var(--spacing-3)' }}>
          {observation.summary ?? '—'}
        </div>
        {observation.images.length > 0 && (
          <div style={{ marginTop: 'var(--spacing-3)' }}>
            <h3 style={{ fontSize: 'var(--font-size-md)', margin: '0 0 6px' }}>Images</h3>
            <ObservationImages observationId={observation.id} tokens={observation.images} />
          </div>
        )}
        {quoted && (
          <div style={{ marginTop: 'var(--spacing-3)', display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', flexWrap: 'wrap', fontSize: 'var(--font-size-sm)' }}>
            <span style={badgeStyleFor(observation.quoteStatus || 'DRAFT')}>{observation.quoteNumber ?? 'Quote'}{observation.quoteStatus ? ` · ${observation.quoteStatus}` : ''}</span>
            <span style={{ color: 'var(--color-text-muted)' }}>
              Quote created{observation.quotedAt ? ` ${bneDateTime(observation.quotedAt)}` : ''}
              {observation.quoteTotal != null ? ` · ${new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(observation.quoteTotal)} inc GST` : ''}
            </span>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--spacing-2)', marginTop: 'var(--spacing-4)' }}>
          {quoted ? (
            <button style={primaryButtonStyle} onClick={() => onOpenQuote(observation.quoteId!)} title="Open the quote created from this request">
              Open {observation.quoteNumber ?? 'quote'}
            </button>
          ) : (
            <>
              <button style={secondaryButtonStyle} disabled={archiving} onClick={onArchive} title="Dismiss this request without creating a quote (e.g. spam or duplicate)">
                {archiving ? 'Dismissing…' : 'Dismiss (no quote)'}
              </button>
              <button style={primaryButtonStyle} onClick={onCreateQuote} title="Create a quote pre-filled with this request's customer and services">
                Create quote
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** Small status pill for a quote's Xero status. */
function badgeStyleFor(status: string): React.CSSProperties {
  const s = status.toUpperCase();
  const [bg, color] =
    s === 'ACCEPTED' || s === 'INVOICED' ? ['rgba(40,167,69,0.12)', 'var(--color-success)']
    : s === 'SENT' ? ['rgba(0,176,151,0.14)', 'var(--color-accent)']
    : s === 'DECLINED' ? ['rgba(220,53,69,0.12)', 'var(--color-error)']
    : ['rgba(108,117,125,0.14)', 'var(--color-text-muted)'];
  return { fontSize: 'var(--font-size-xs)', fontWeight: 700, backgroundColor: bg, color, border: `1px solid ${color}`, borderRadius: 6, padding: '2px 8px', verticalAlign: 'middle' };
}
